/**
 * Elite Batch 14 Part 2 — Workflow Templates 1268–1287
 * API rate limiting, form abandonment, access reviews, account setup,
 * alumni engagement, approval workflows, reference checks, report generation,
 * standup collection, tech debt tracking, birthday reminders, board meetings,
 * board resolutions, BCP testing, CSV processing, calendar booking, and task creation.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

/* ================================================================
 *  TEMPLATE 1268 — API Rate Limit Monitor
 * ================================================================ */
const wf_1268 = {
  nodes: [
    trigger("API Quota Check Schedule", "schedule", {
      schedule: "Every 5 minutes",
      howItStarts:
        "Runs every 5 minutes to monitor API rate limit usage across all integrated services",
      setup:
        "Configure schedule for 5-minute intervals. Ensure API keys for monitored services are set.",
      inputFields: [],
    }),
    // Node 2: Multi-API quota fetch
    n("2", "runCode", "Fetch API Quota Usage", 160, {
      runCodeDetails: {
        code: `const apis = [
  { name: "OpenAI", url: "https://api.openai.com/v1/usage", header: "x-ratelimit-remaining-requests" },
  { name: "SendGrid", url: "https://api.sendgrid.com/v3/user/credits", header: "x-ratelimit-remaining" },
  { name: "HubSpot", url: "https://api.hubapi.com/integrations/v1/limit/daily", header: "x-hubspot-ratelimit-daily-remaining" }
];
const results = apis.map(api => ({
  service: api.name,
  remaining: Math.floor(Math.random() * 1000), // placeholder — real impl reads headers
  limit: 10000,
  usage_pct: 0
}));
results.forEach(r => { r.usage_pct = Math.round(((r.limit - r.remaining) / r.limit) * 100); });
const highest_usage = Math.max(...results.map(r => r.usage_pct));
const critical_service = results.find(r => r.usage_pct === highest_usage)?.service || "unknown";
return { api_usage_report: JSON.stringify(results), highest_usage_pct: highest_usage, critical_service, total_apis_checked: results.length };`,
        outputs: [
          { name: "api_usage_report", type: "string" },
          { name: "highest_usage_pct", type: "number" },
          { name: "critical_service", type: "string" },
          { name: "total_apis_checked", type: "number" },
        ],
      },
    }),
    // Node 3: Threshold evaluation
    n("3", "runCode", "Evaluate Rate Limit Thresholds", 280, {
      runCodeDetails: {
        code: `const report = JSON.parse("{api_usage_report}");
const warnings = report.filter(r => r.usage_pct >= 70 && r.usage_pct < 90);
const critical = report.filter(r => r.usage_pct >= 90);
const severity = critical.length > 0 ? "critical" : warnings.length > 0 ? "warning" : "healthy";
const needs_pagerduty = critical.length > 0;
return { severity, warning_count: warnings.length, critical_count: critical.length, needs_pagerduty, threshold_summary: severity === "healthy" ? "All APIs within normal limits" : critical.map(c => c.service + " at " + c.usage_pct + "%").join(", ") };`,
        outputs: [
          { name: "severity", type: "string" },
          { name: "warning_count", type: "number" },
          { name: "critical_count", type: "number" },
          { name: "needs_pagerduty", type: "boolean" },
          { name: "threshold_summary", type: "string" },
        ],
      },
    }),
    // Node 4: AI trend analysis
    aiNode(
      "4",
      "AI Rate Limit Trend Analysis",
      400,
      "You are an API operations analyst. Analyze rate limit usage patterns and predict when limits will be hit. Return JSON with fields: trend_direction (string: increasing/stable/decreasing), predicted_exhaustion_hours (number or null), recommendation (string), risk_level (string: low/medium/high/critical).",
      "Current API usage data: {api_usage_report}. Severity: {severity}. Critical services: {threshold_summary}. Provide trend analysis and recommendations.",
      [
        { field: "trend_direction", as: "trend_direction", type: "string" },
        {
          field: "predicted_exhaustion_hours",
          as: "predicted_exhaustion_hours",
          type: "string",
        },
        { field: "recommendation", as: "ai_recommendation", type: "string" },
        { field: "risk_level", as: "risk_level", type: "string" },
      ],
    ),
    // Node 5: Developer Slack alert
    slackNode(
      "5",
      "Alert Dev Team on Slack",
      520,
      ":warning: *API Rate Limit Alert*\\nSeverity: {severity}\\nCritical Service: {critical_service} ({highest_usage_pct}% used)\\nTrend: {trend_direction}\\nPredicted Exhaustion: {predicted_exhaustion_hours}\\nRecommendation: {ai_recommendation}",
    ),
    // Node 6: PagerDuty escalation condition
    n("6", "condition", "Needs PagerDuty Escalation?", 640, {
      conditionDetails: {
        rules: [
          { field: "{needs_pagerduty}", operator: "equals", value: "true" },
        ],
      },
    }),
    // Node 7 (True): Rate limit dashboard Sheets
    sheetsNode(
      "7",
      "Log to Rate Limit Dashboard",
      760,
      "RateLimits",
      `[["{critical_service}","{highest_usage_pct}","{severity}","{trend_direction}","{ai_recommendation}","${new Date().toISOString()}"]]`,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7", "output_1"), // True: needs PagerDuty — log critical + escalate
    e("6", "7", "output_2"), // False: still log for tracking
  ],
  summary:
    "Monitors API rate limits every 5 minutes across all integrated services, evaluates thresholds, uses AI to predict exhaustion trends, alerts developers on Slack, and conditionally escalates to PagerDuty for critical usage levels.",
};

/* ================================================================
 *  TEMPLATE 1269 — Abandoned Form Follow Up
 * ================================================================ */
const wf_1269 = {
  nodes: [
    trigger("Form Abandonment Webhook", "webhook_app", {
      source: "form_tracker",
      event: "form.abandoned",
      howItStarts:
        "Fires when a user starts filling out a form but leaves without submitting",
      setup:
        "Configure your form tracking tool (e.g., Formisimo, Hotjar) to send a webhook when form abandonment is detected.",
      inputFields: [],
    }),
    // Node 2: AI extract partial data
    aiNode(
      "2",
      "Extract Partial Form Data",
      160,
      "You are a form data extraction specialist. Extract whatever partial data was captured from the abandoned form payload. Find the email, name, last field completed, time spent, form URL, and form name regardless of payload format. Return JSON with fields: partial_email (string or empty), partial_name (string or empty), last_field_completed (string), time_on_form_seconds (number), form_url (string), form_name (string), fields_completed_count (number), total_fields (number).",
      "Abandoned form webhook payload: {webhook_payload}",
      [
        { field: "partial_email", as: "partial_email", type: "string" },
        { field: "partial_name", as: "partial_name", type: "string" },
        {
          field: "last_field_completed",
          as: "last_field_completed",
          type: "string",
        },
        {
          field: "time_on_form_seconds",
          as: "time_on_form_seconds",
          type: "number",
        },
        { field: "form_url", as: "form_url", type: "string" },
        { field: "form_name", as: "form_name", type: "string" },
        {
          field: "fields_completed_count",
          as: "fields_completed_count",
          type: "number",
        },
        { field: "total_fields", as: "total_fields", type: "number" },
      ],
    ),
    // Node 3: Identification branch — do we have an email?
    n("3", "condition", "Email Captured?", 300, {
      conditionDetails: {
        rules: [
          { field: "{partial_email}", operator: "not_equals", value: "" },
        ],
      },
    }),
    // Node 4 (True path): AI abandonment reason analysis
    aiNode(
      "4",
      "Analyze Abandonment Reason",
      420,
      "You are a UX conversion analyst. Analyze form abandonment data and determine the likely reason for abandonment and generate a personalized follow-up message. Return JSON with fields: abandonment_reason (string), follow_up_subject (string), follow_up_body (string), urgency (string: low/medium/high), saved_progress_summary (string).",
      "Form: {form_name}. Email: {partial_email}. Name: {partial_name}. Last field: {last_field_completed}. Time spent: {time_on_form_seconds}s. Fields completed: {fields_completed_count}/{total_fields}. Generate a follow-up strategy.",
      [
        {
          field: "abandonment_reason",
          as: "abandonment_reason",
          type: "string",
        },
        { field: "follow_up_subject", as: "follow_up_subject", type: "string" },
        { field: "follow_up_body", as: "follow_up_body", type: "string" },
        { field: "urgency", as: "urgency", type: "string" },
        {
          field: "saved_progress_summary",
          as: "saved_progress_summary",
          type: "string",
        },
      ],
    ),
    // Node 5: Follow-up email with saved progress
    emailNode(
      "5",
      "Send Follow-Up Email",
      540,
      "{partial_email}",
      "{follow_up_subject}",
      "{follow_up_body}<br><br><strong>Your saved progress:</strong> {saved_progress_summary}<br><a href='{form_url}?resume=true'>Continue where you left off</a>",
    ),
    // Node 6: Retargeting pixel fire
    n("6", "hitApi", "Fire Retargeting Pixel", 660, {
      hitApiDetails: {
        method: "POST",
        url: "https://graph.facebook.com/v18.0/{pixel_id}/events",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [
          {
            key: "data",
            value: `[{"event_name":"FormAbandonment","event_time":${Math.floor(Date.now() / 1000)},"user_data":{"em":"{partial_email}"},"custom_data":{"form_name":"{form_name}","fields_completed":"{fields_completed_count}","abandonment_reason":"{abandonment_reason}"}}]`,
          },
          { key: "access_token", value: "{fb_access_token}" },
        ],
        variables: [
          {
            name: "events_received",
            customName: "pixel_events_received",
            type: "number",
          },
        ],
      },
    }),
    // Node 7: Manager notification Slack
    slackNode(
      "7",
      "Notify Sales Manager",
      780,
      ":clipboard: *Form Abandonment Alert*\\nForm: {form_name}\\nContact: {partial_name} ({partial_email})\\nProgress: {fields_completed_count}/{total_fields} fields\\nLast field: {last_field_completed}\\nReason: {abandonment_reason}\\nUrgency: {urgency}",
    ),
    // Node 8: Completion tracking Sheets
    sheetsNode(
      "8",
      "Log Abandonment to Tracker",
      900,
      "FormAbandonment",
      `[["{partial_email}","{form_name}","{fields_completed_count}","{total_fields}","{last_field_completed}","{abandonment_reason}","{urgency}","${new Date().toISOString()}"]]`,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4", "output_1"), // True: email captured — personalize
    e("3", "7", "output_2"), // False: no email — just notify manager
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8"),
  ],
  summary:
    "Captures form abandonment events, extracts partial data using AI, sends personalized follow-up emails with saved progress links, fires retargeting pixels, alerts sales managers, and tracks all abandonments in a spreadsheet.",
};

/* ================================================================
 *  TEMPLATE 1270 — Access Review and Certification Bot
 * ================================================================ */
const wf_1270 = {
  nodes: [
    trigger("Quarterly Access Review", "schedule", {
      schedule: "First Monday of every quarter at 9:00 AM",
      howItStarts:
        "Runs quarterly to initiate access reviews for all users and their system permissions",
      setup:
        "Configure schedule for quarterly execution. Set up your identity provider (Okta/Azure AD) API credentials.",
      inputFields: [],
    }),
    // Node 2: User access inventory pull
    n("2", "hitApi", "Pull User Access Inventory", 160, {
      hitApiDetails: {
        method: "GET",
        url: 'https://{okta_domain}/api/v1/users?limit=200&filter=status eq "ACTIVE"',
        headers: [
          { key: "Authorization", value: "SSWS {okta_api_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          { name: "length", customName: "total_users", type: "number" },
          { name: "[0].id", customName: "first_user_id", type: "string" },
          {
            name: "[0].profile.email",
            customName: "sample_user_email",
            type: "string",
          },
        ],
      },
    }),
    // Node 3: Manager assignment lookup
    n("3", "runCode", "Build Manager Review Assignments", 280, {
      runCodeDetails: {
        code: `const totalUsers = parseInt("{total_users}") || 0;
const reviewGroups = [];
const managersNeeded = Math.ceil(totalUsers / 10);
for (let i = 0; i < managersNeeded; i++) {
  reviewGroups.push({
    manager_index: i,
    review_count: Math.min(10, totalUsers - (i * 10)),
    review_id: "AR-Q" + new Date().getFullYear() + "-" + String(i + 1).padStart(3, "0")
  });
}
const review_period_id = "AR-" + new Date().getFullYear() + "Q" + (Math.ceil((new Date().getMonth() + 1) / 3));
return { review_assignments: JSON.stringify(reviewGroups), total_reviews: reviewGroups.length, review_period_id, total_users_in_scope: totalUsers };`,
        outputs: [
          { name: "review_assignments", type: "string" },
          { name: "total_reviews", type: "number" },
          { name: "review_period_id", type: "string" },
          { name: "total_users_in_scope", type: "number" },
        ],
      },
    }),
    // Node 4: Review request distribution email
    emailNode(
      "4",
      "Send Review Requests to Managers",
      400,
      "{manager_email_list}",
      "Action Required: Quarterly Access Review {review_period_id}",
      "Hello,<br><br>The quarterly access review period <strong>{review_period_id}</strong> has begun. You have <strong>{total_reviews}</strong> user access reviews to complete.<br><br>Total users in scope: {total_users_in_scope}<br><br>Please review each user's access and certify or revoke permissions by the deadline.<br><br><a href='https://app.omniengage.com/access-review/{review_period_id}'>Start Your Reviews</a><br><br>Deadline: 7 days from today.",
    ),
    // Node 5: Delay 7 days
    n("5", "delayAction", "Wait 7 Days for Responses", 520, {
      delayDetails: { duration: "7", unit: "days" },
    }),
    // Node 6: Response collection condition
    n("6", "condition", "All Reviews Completed?", 640, {
      conditionDetails: {
        rules: [
          {
            field: "{total_reviews}",
            operator: "equals",
            value: "{completed_reviews}",
          },
        ],
      },
    }),
    // Node 7 (both paths): AI anomaly flagging
    aiNode(
      "7",
      "AI Anomaly Detection in Access",
      760,
      "You are an IT security access review analyst. Analyze access review results for anomalies such as excessive permissions, dormant accounts with high access, cross-department access without justification, and privilege escalation patterns. Return JSON with fields: anomalies_found (number), high_risk_users (string — comma-separated list), recommendations (string), compliance_score (number 0-100), review_summary (string).",
      "Review period: {review_period_id}. Total users reviewed: {total_users_in_scope}. Review completion data: {review_assignments}. Flag any access anomalies.",
      [
        { field: "anomalies_found", as: "anomalies_found", type: "number" },
        { field: "high_risk_users", as: "high_risk_users", type: "string" },
        {
          field: "recommendations",
          as: "access_recommendations",
          type: "string",
        },
        { field: "compliance_score", as: "compliance_score", type: "number" },
        { field: "review_summary", as: "review_summary", type: "string" },
      ],
    ),
    // Node 8: Compliance report Sheets
    sheetsNode(
      "8",
      "Log Compliance Report",
      880,
      "AccessReviews",
      `[["{review_period_id}","{total_users_in_scope}","{anomalies_found}","{compliance_score}","{high_risk_users}","{access_recommendations}","${new Date().toISOString()}"]]`,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7", "output_1"), // True: all completed — run anomaly detection
    e("6", "7", "output_2"), // False: incomplete — still analyze what we have
    e("7", "8"),
  ],
  summary:
    "Runs quarterly access reviews by pulling user inventories from Okta, assigning reviews to managers, waiting for completion, using AI to detect access anomalies and privilege escalation risks, and logging compliance reports.",
};

/* ================================================================
 *  TEMPLATE 1271 — Account Setup Assistant
 * ================================================================ */
const wf_1271 = {
  nodes: [
    trigger("New Account Created Webhook", "webhook_app", {
      source: "billing_system",
      event: "account.created",
      howItStarts:
        "Fires when a new customer account is created in the billing system",
      setup:
        "Configure your billing system (Stripe, Chargebee, etc.) to send account creation webhooks to this endpoint.",
      inputFields: [],
    }),
    // Node 2: AI extract account data
    aiNode(
      "2",
      "Extract Account Details",
      160,
      "You are an account data extraction specialist. Extract the new account information from the webhook payload regardless of format. Return JSON with fields: account_id (string), company_name (string), admin_email (string), admin_name (string), plan_name (string), industry (string or empty), team_size (string or empty).",
      "New account webhook payload: {webhook_payload}",
      [
        { field: "account_id", as: "account_id", type: "string" },
        { field: "company_name", as: "company_name", type: "string" },
        { field: "admin_email", as: "admin_email", type: "string" },
        { field: "admin_name", as: "admin_name", type: "string" },
        { field: "plan_name", as: "plan_name", type: "string" },
        { field: "industry", as: "industry", type: "string" },
        { field: "team_size", as: "team_size", type: "string" },
      ],
    ),
    // Node 3: Account data validation
    n("3", "runCode", "Validate Account Data", 280, {
      runCodeDetails: {
        code: `const email = "{admin_email}";
const accountId = "{account_id}";
const companyName = "{company_name}";
const emailValid = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(email);
const idValid = accountId && accountId.length > 0 && accountId !== "undefined";
const nameValid = companyName && companyName.length > 1;
const validation_passed = emailValid && idValid && nameValid;
const issues = [];
if (!emailValid) issues.push("Invalid email format");
if (!idValid) issues.push("Missing account ID");
if (!nameValid) issues.push("Missing company name");
return { validation_passed, validation_issues: issues.join(", ") || "None", validated_email: email, validated_account_id: accountId };`,
        outputs: [
          { name: "validation_passed", type: "boolean" },
          { name: "validation_issues", type: "string" },
          { name: "validated_email", type: "string" },
          { name: "validated_account_id", type: "string" },
        ],
      },
    }),
    // Node 4: Service provisioning API
    n("4", "hitApi", "Provision Account Services", 400, {
      hitApiDetails: {
        method: "POST",
        url: "https://api.omniengage.com/v1/accounts/{validated_account_id}/provision",
        headers: [
          { key: "Authorization", value: "Bearer {internal_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "account_id", value: "{validated_account_id}" },
          { key: "plan", value: "{plan_name}" },
          { key: "features", value: '["email","sms","workflows","analytics"]' },
          { key: "admin_email", value: "{validated_email}" },
        ],
        variables: [
          {
            name: "provisioned",
            customName: "provisioning_success",
            type: "boolean",
          },
          {
            name: "workspace_url",
            customName: "workspace_url",
            type: "string",
          },
          { name: "api_key", customName: "generated_api_key", type: "string" },
        ],
      },
    }),
    // Node 5: Welcome onboarding email
    emailNode(
      "5",
      "Send Welcome Onboarding Email",
      520,
      "{validated_email}",
      "Welcome to OmniEngage, {admin_name}! Your account is ready",
      "Hi {admin_name},<br><br>Welcome to OmniEngage! Your account for <strong>{company_name}</strong> has been set up successfully.<br><br><strong>Plan:</strong> {plan_name}<br><strong>Workspace:</strong> <a href='{workspace_url}'>{workspace_url}</a><br><br>Here are your next steps:<br>1. Log in and complete your profile<br>2. Invite your team members<br>3. Connect your first integration<br>4. Create your first workflow<br><br>Need help? Reply to this email or check our docs.",
    ),
    // Node 6: Delay 30 minutes
    n("6", "delayAction", "Wait 30 Minutes", 640, {
      delayDetails: { duration: "30", unit: "minutes" },
    }),
    // Node 7: Setup verification condition
    n("7", "condition", "Account Setup Completed?", 760, {
      conditionDetails: {
        rules: [
          {
            field: "{provisioning_success}",
            operator: "equals",
            value: "true",
          },
        ],
      },
    }),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Automates new account setup by extracting account data from webhooks, validating fields, provisioning services via API, sending personalized welcome emails, and verifying setup completion after a 30-minute delay.",
};

/* ================================================================
 *  TEMPLATE 1272 — Alumni Database Updater
 * ================================================================ */
const wf_1272 = {
  nodes: [
    trigger("Monthly Alumni Data Refresh", "schedule", {
      schedule: "First day of every month at 6:00 AM",
      howItStarts:
        "Runs monthly to enrich and update alumni contact records with current information",
      setup:
        "Configure schedule for monthly execution. Set up LinkedIn API, email verification API, and HubSpot credentials.",
      inputFields: [],
    }),
    // Node 2: Alumni record extraction
    hubspotGetNode(
      "2",
      "Fetch Alumni Records from CRM",
      160,
      'crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,company,jobtitle,graduation_year&filterGroups=[{"filters":[{"propertyName":"contact_type","operator":"EQ","value":"alumni"}]}]',
      [
        { name: "total", customName: "total_alumni", type: "number" },
        { name: "results.length", customName: "batch_size", type: "number" },
        { name: "results", customName: "alumni_records", type: "string" },
      ],
    ),
    // Node 3: LinkedIn profile enrichment
    n("3", "hitApi", "Enrich via LinkedIn API", 280, {
      hitApiDetails: {
        method: "POST",
        url: "https://api.proxycurl.com/api/v2/linkedin/company/employee/search",
        headers: [
          { key: "Authorization", value: "Bearer {proxycurl_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "linkedin_company_profile_url",
            value: "https://linkedin.com/company/{alumni_company}",
          },
          { key: "keyword_regex", value: "{alumni_name_pattern}" },
        ],
        variables: [
          {
            name: "results.length",
            customName: "linkedin_matches",
            type: "number",
          },
          {
            name: "results[0].linkedin_profile_url",
            customName: "linkedin_url",
            type: "string",
          },
          {
            name: "results[0].current_company",
            customName: "current_company",
            type: "string",
          },
          {
            name: "results[0].headline",
            customName: "current_title",
            type: "string",
          },
        ],
      },
    }),
    // Node 4: Email verification
    n("4", "hitApi", "Verify Email Addresses", 400, {
      hitApiDetails: {
        method: "GET",
        url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_key}&email={alumni_email}",
        headers: [],
        body: [],
        variables: [
          { name: "status", customName: "email_status", type: "string" },
          {
            name: "sub_status",
            customName: "email_sub_status",
            type: "string",
          },
          {
            name: "did_you_mean",
            customName: "email_suggestion",
            type: "string",
          },
        ],
      },
    }),
    // Node 5: Change detection
    n("5", "runCode", "Detect Record Changes", 520, {
      runCodeDetails: {
        code: `const currentCompany = "{current_company}";
const currentTitle = "{current_title}";
const emailStatus = "{email_status}";
const linkedinUrl = "{linkedin_url}";
const changes = [];
if (currentCompany && currentCompany !== "undefined" && currentCompany !== "") changes.push("company:" + currentCompany);
if (currentTitle && currentTitle !== "undefined" && currentTitle !== "") changes.push("title:" + currentTitle);
if (linkedinUrl && linkedinUrl !== "undefined") changes.push("linkedin:" + linkedinUrl);
if (emailStatus === "invalid") changes.push("email:bounced");
const has_changes = changes.length > 0;
const needs_email_update = emailStatus === "invalid" || emailStatus === "catch-all";
return { has_changes, change_list: changes.join("; ") || "No changes detected", changes_count: changes.length, needs_email_update, enrichment_source: "LinkedIn+ZeroBounce" };`,
        outputs: [
          { name: "has_changes", type: "boolean" },
          { name: "change_list", type: "string" },
          { name: "changes_count", type: "number" },
          { name: "needs_email_update", type: "boolean" },
          { name: "enrichment_source", type: "string" },
        ],
      },
    }),
    // Node 6: Database update execution
    hubspotPostNode(
      "6",
      "Update Alumni in HubSpot",
      640,
      "/crm/v3/objects/contacts/{alumni_contact_id}",
      [
        {
          key: "properties",
          value: `{"company":"{current_company}","jobtitle":"{current_title}","linkedin_url":"{linkedin_url}","last_enrichment_date":"${new Date().toISOString().split("T")[0]}","enrichment_source":"{enrichment_source}"}`,
        },
      ],
      [
        { name: "id", customName: "updated_contact_id", type: "string" },
        {
          name: "properties.email",
          customName: "updated_email",
          type: "string",
        },
      ],
    ),
    // Node 7: Update summary report
    sheetsNode(
      "7",
      "Log Update Summary",
      760,
      "AlumniUpdates",
      `[["{total_alumni}","{changes_count}","{change_list}","{enrichment_source}","${new Date().toISOString()}"]]`,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Keeps alumni databases current by monthly pulling records from HubSpot, enriching with LinkedIn profile data, verifying email addresses, detecting changes, updating CRM records, and logging a summary report.",
};

/* ================================================================
 *  TEMPLATE 1273 — Alumni Engagement Campaign
 * ================================================================ */
const wf_1273 = {
  nodes: [
    trigger("Alumni Engagement Campaign Schedule", "schedule", {
      schedule: "Every Monday at 10:00 AM",
      howItStarts:
        "Runs weekly to segment alumni and send personalized multi-channel engagement campaigns",
      setup:
        "Configure schedule for weekly execution. Set up HubSpot, email, and SMS credentials.",
      inputFields: [],
    }),
    // Node 2: Alumni segmentation
    hubspotGetNode(
      "2",
      "Segment Alumni by Engagement",
      160,
      'crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,graduation_year,last_engagement_date,engagement_score,phone&filterGroups=[{"filters":[{"propertyName":"contact_type","operator":"EQ","value":"alumni"}]}]',
      [
        { name: "total", customName: "total_alumni", type: "number" },
        { name: "results", customName: "alumni_segments", type: "string" },
        {
          name: "results[0].properties.email",
          customName: "first_alumni_email",
          type: "string",
        },
        {
          name: "results[0].properties.firstname",
          customName: "first_alumni_name",
          type: "string",
        },
        {
          name: "results[0].properties.phone",
          customName: "first_alumni_phone",
          type: "string",
        },
        {
          name: "results[0].properties.engagement_score",
          customName: "first_engagement_score",
          type: "string",
        },
      ],
    ),
    // Node 3: AI content personalization
    aiNode(
      "3",
      "Generate Personalized Content",
      300,
      "You are an alumni engagement specialist. Generate personalized email and SMS content for alumni campaigns based on their segment data. Return JSON with fields: email_subject (string), email_body_html (string), sms_message (string, max 160 chars), content_theme (string), personalization_level (string: high/medium/low).",
      "Alumni data: Name: {first_alumni_name}, Engagement score: {first_engagement_score}, Total alumni in campaign: {total_alumni}. Create engaging reunion/networking content.",
      [
        {
          field: "email_subject",
          as: "campaign_email_subject",
          type: "string",
        },
        { field: "email_body_html", as: "campaign_email_body", type: "string" },
        { field: "sms_message", as: "campaign_sms_message", type: "string" },
        { field: "content_theme", as: "content_theme", type: "string" },
        {
          field: "personalization_level",
          as: "personalization_level",
          type: "string",
        },
      ],
    ),
    // Node 4: Email dispatch
    emailNode(
      "4",
      "Send Campaign Email",
      440,
      "{first_alumni_email}",
      "{campaign_email_subject}",
      "{campaign_email_body}",
    ),
    // Node 5: SMS dispatch
    smsNode(
      "5",
      "Send Campaign SMS",
      560,
      "{first_alumni_phone}",
      "{campaign_sms_message}",
    ),
    // Node 6: Delay 7 days
    n("6", "delayAction", "Wait 7 Days for Engagement", 680, {
      delayDetails: { duration: "7", unit: "days" },
    }),
    // Node 7: Engagement scoring condition
    n("7", "condition", "High Engagement Response?", 800, {
      conditionDetails: {
        rules: [
          {
            field: "{first_engagement_score}",
            operator: "greater_than",
            value: "50",
          },
        ],
      },
    }),
    // Node 8: Campaign analytics
    sheetsNode(
      "8",
      "Log Campaign Analytics",
      920,
      "AlumniCampaigns",
      `[["{content_theme}","{total_alumni}","{personalization_level}","{first_engagement_score}","email+sms","${new Date().toISOString()}"]]`,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_1"), // True: high engagement — log success
    e("7", "8", "output_2"), // False: low engagement — still log for analysis
  ],
  summary:
    "Runs weekly alumni engagement campaigns by segmenting contacts in HubSpot, generating AI-personalized email and SMS content, dispatching across channels, waiting for engagement, and tracking campaign analytics.",
};

/* ================================================================
 *  TEMPLATE 1274 — Approval Workflow
 * ================================================================ */
const wf_1274 = {
  nodes: [
    trigger("Approval Request Submitted", "webhook_app", {
      source: "internal_app",
      event: "request.submitted",
      howItStarts:
        "Fires when an employee submits a request requiring managerial or cross-functional approval",
      setup:
        "Configure your internal request system to send webhooks when new approval requests are submitted.",
      inputFields: [],
    }),
    // Node 2: AI extract request data
    aiNode(
      "2",
      "Extract Request Details",
      160,
      "You are a request processing specialist. Extract the approval request details from the webhook payload regardless of format. Return JSON with fields: request_id (string), requester_name (string), requester_email (string), request_type (string — e.g. purchase, time-off, access, budget), amount (number or 0), description (string), department (string), urgency (string: low/medium/high).",
      "Approval request webhook payload: {webhook_payload}",
      [
        { field: "request_id", as: "request_id", type: "string" },
        { field: "requester_name", as: "requester_name", type: "string" },
        { field: "requester_email", as: "requester_email", type: "string" },
        { field: "request_type", as: "request_type", type: "string" },
        { field: "amount", as: "request_amount", type: "number" },
        { field: "description", as: "request_description", type: "string" },
        { field: "department", as: "department", type: "string" },
        { field: "urgency", as: "urgency", type: "string" },
      ],
    ),
    // Node 3: Approval routing logic
    n("3", "runCode", "Determine Approval Chain", 280, {
      runCodeDetails: {
        code: `const requestType = "{request_type}";
const amount = parseFloat("{request_amount}") || 0;
const department = "{department}";
let approver_email = "";
let approval_level = "manager";
if (amount > 10000) {
  approver_email = "vp-finance@company.com";
  approval_level = "vp";
} else if (amount > 5000) {
  approver_email = "director-" + department.toLowerCase() + "@company.com";
  approval_level = "director";
} else {
  approver_email = "manager-" + department.toLowerCase() + "@company.com";
  approval_level = "manager";
}
if (requestType === "access") {
  approver_email = "it-security@company.com";
  approval_level = "security";
}
const sla_hours = amount > 10000 ? 72 : 48;
return { approver_email, approval_level, sla_hours, routing_reason: approval_level + " approval required for " + requestType + " ($" + amount + ")" };`,
        outputs: [
          { name: "approver_email", type: "string" },
          { name: "approval_level", type: "string" },
          { name: "sla_hours", type: "number" },
          { name: "routing_reason", type: "string" },
        ],
      },
    }),
    // Node 4: Approver notification email
    emailNode(
      "4",
      "Notify Approver",
      400,
      "{approver_email}",
      "[{urgency}] Approval Needed: {request_type} request #{request_id}",
      "Hello,<br><br>A new <strong>{request_type}</strong> request requires your approval.<br><br><strong>Request ID:</strong> {request_id}<br><strong>Requester:</strong> {requester_name} ({department})<br><strong>Amount:</strong> ${request_amount}<br><strong>Description:</strong> {request_description}<br><strong>Urgency:</strong> {urgency}<br><strong>Routing:</strong> {routing_reason}<br><br><a href='https://app.omniengage.com/approvals/{request_id}/approve'>Approve</a> | <a href='https://app.omniengage.com/approvals/{request_id}/deny'>Deny</a><br><br>SLA: Please respond within {sla_hours} hours.",
    ),
    // Node 5: Delay 3 days
    n("5", "delayAction", "Wait 3 Days for Decision", 520, {
      delayDetails: { duration: "3", unit: "days" },
    }),
    // Node 6: Decision branch
    n("6", "condition", "Approval Decision Received?", 640, {
      conditionDetails: {
        rules: [
          { field: "{approval_level}", operator: "not_equals", value: "" },
        ],
      },
    }),
    // Node 7: Audit trail logging
    sheetsNode(
      "7",
      "Log to Approval Audit Trail",
      760,
      "ApprovalAudit",
      `[["{request_id}","{requester_name}","{request_type}","{request_amount}","{approver_email}","{approval_level}","{urgency}","${new Date().toISOString()}"]]`,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7", "output_1"), // True: decision received — log it
    e("6", "7", "output_2"), // False: no decision — still log for SLA tracking
  ],
  summary:
    "Processes approval requests by extracting details via AI, routing to the correct approver based on amount and type, sending notification emails with approve/deny links, tracking SLA compliance, and maintaining a full audit trail.",
};

/* ================================================================
 *  TEMPLATE 1275 — Automated Reference Check Bot
 * ================================================================ */
const wf_1275 = {
  nodes: [
    trigger("Reference Check Request", "webhook_app", {
      source: "ats_system",
      event: "reference_check.requested",
      howItStarts:
        "Fires when a recruiter initiates a reference check for a candidate in the ATS",
      setup:
        "Configure your ATS (Greenhouse, Lever, etc.) to send webhooks when a reference check is requested.",
      inputFields: [],
    }),
    // Node 2: AI extract candidate data
    aiNode(
      "2",
      "Extract Candidate & Referee Data",
      160,
      "You are an HR data extraction specialist. Extract reference check details from the webhook payload regardless of format. Return JSON with fields: candidate_name (string), candidate_email (string), position_applied (string), referee_name (string), referee_email (string), referee_relationship (string), referee_company (string), application_id (string).",
      "Reference check request payload: {webhook_payload}",
      [
        { field: "candidate_name", as: "candidate_name", type: "string" },
        { field: "candidate_email", as: "candidate_email", type: "string" },
        { field: "position_applied", as: "position_applied", type: "string" },
        { field: "referee_name", as: "referee_name", type: "string" },
        { field: "referee_email", as: "referee_email", type: "string" },
        {
          field: "referee_relationship",
          as: "referee_relationship",
          type: "string",
        },
        { field: "referee_company", as: "referee_company", type: "string" },
        { field: "application_id", as: "application_id", type: "string" },
      ],
    ),
    // Node 3: Referee outreach email
    emailNode(
      "3",
      "Send Reference Request to Referee",
      300,
      "{referee_email}",
      "Reference Request for {candidate_name} — {position_applied}",
      "Dear {referee_name},<br><br>{candidate_name} has listed you as a professional reference for the position of <strong>{position_applied}</strong> at our company. Your relationship: <strong>{referee_relationship}</strong>.<br><br>We would greatly appreciate if you could complete a brief reference questionnaire:<br><br><a href='https://app.omniengage.com/reference/{application_id}?ref={referee_email}'>Complete Reference Check</a><br><br>This should take approximately 5-10 minutes. Your responses are confidential.<br><br>Please complete this within 5 business days.<br><br>Thank you for your time.",
    ),
    // Node 4: Delay 5 days
    n("4", "delayAction", "Wait 5 Days for Response", 440, {
      delayDetails: { duration: "5", unit: "days" },
    }),
    // Node 5: Response collection condition
    n("5", "condition", "Reference Response Received?", 560, {
      conditionDetails: {
        rules: [
          { field: "{referee_email}", operator: "not_equals", value: "" },
        ],
      },
    }),
    // Node 6 (True): AI response analysis
    aiNode(
      "6",
      "Analyze Reference Responses",
      680,
      "You are an HR reference analysis specialist. Analyze reference check responses and generate a structured assessment. Return JSON with fields: overall_rating (string: strong/positive/neutral/concerning), key_strengths (string), areas_of_concern (string), culture_fit_assessment (string), recommendation_summary (string), red_flags (string or empty), confidence_score (number 1-10).",
      "Reference for: {candidate_name} applying for {position_applied}. Referee: {referee_name} ({referee_relationship} at {referee_company}). Application ID: {application_id}. Generate reference analysis.",
      [
        { field: "overall_rating", as: "overall_rating", type: "string" },
        { field: "key_strengths", as: "key_strengths", type: "string" },
        { field: "areas_of_concern", as: "areas_of_concern", type: "string" },
        {
          field: "recommendation_summary",
          as: "recommendation_summary",
          type: "string",
        },
        { field: "red_flags", as: "red_flags", type: "string" },
        { field: "confidence_score", as: "confidence_score", type: "number" },
      ],
    ),
    // Node 7 (True → report): Reference report email to recruiter
    emailNode(
      "7",
      "Send Reference Report to Recruiter",
      800,
      "{recruiter_email}",
      "Reference Report: {candidate_name} — {position_applied} [{overall_rating}]",
      "Reference check complete for <strong>{candidate_name}</strong> ({position_applied}).<br><br><strong>Referee:</strong> {referee_name} ({referee_relationship})<br><strong>Overall Rating:</strong> {overall_rating}<br><strong>Confidence Score:</strong> {confidence_score}/10<br><br><strong>Key Strengths:</strong> {key_strengths}<br><strong>Areas of Concern:</strong> {areas_of_concern}<br><strong>Recommendation:</strong> {recommendation_summary}<br><strong>Red Flags:</strong> {red_flags}<br><br>Application ID: {application_id}",
    ),
    // Node 8 (False): Non-response escalation Slack
    slackNode(
      "8",
      "Escalate Non-Response",
      680,
      ":warning: *Reference Check Non-Response*\\nCandidate: {candidate_name} ({position_applied})\\nReferee: {referee_name} ({referee_email})\\nRelationship: {referee_relationship}\\nApplication ID: {application_id}\\nAction needed: Follow up manually or request alternate reference.",
      300,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6", "output_1"), // True: response received
    e("5", "8", "output_2"), // False: no response — escalate
    e("6", "7"),
  ],
  summary:
    "Automates reference checks by extracting candidate data, emailing referees with questionnaire links, waiting for responses, using AI to analyze reference quality, generating structured reports for recruiters, and escalating non-responses via Slack.",
};

/* ================================================================
 *  TEMPLATE 1276 — Automated Report Generator
 * ================================================================ */
const wf_1276 = {
  nodes: [
    trigger("Weekly Report Generation", "schedule", {
      schedule: "Every Monday at 7:00 AM",
      howItStarts:
        "Runs weekly to generate automated business reports from multiple data sources",
      setup:
        "Configure schedule for weekly execution. Set up API credentials for all data sources (analytics, CRM, finance).",
      inputFields: [],
    }),
    // Node 2: Multi-source data collection
    n("2", "runCode", "Collect Data from Multiple Sources", 160, {
      runCodeDetails: {
        code: `const dataSources = {
  analytics: { source: "Google Analytics", endpoint: "/v4/reports", status: "pending" },
  crm: { source: "HubSpot", endpoint: "/crm/v3/objects/deals", status: "pending" },
  finance: { source: "Stripe", endpoint: "/v1/balance", status: "pending" },
  support: { source: "Zendesk", endpoint: "/api/v2/tickets/count", status: "pending" }
};
const collectedSources = Object.keys(dataSources).length;
const reportPeriod = new Date();
reportPeriod.setDate(reportPeriod.getDate() - 7);
const period_start = reportPeriod.toISOString().split("T")[0];
const period_end = new Date().toISOString().split("T")[0];
return { data_sources: JSON.stringify(dataSources), collected_sources: collectedSources, period_start, period_end, collection_status: "complete" };`,
        outputs: [
          { name: "data_sources", type: "string" },
          { name: "collected_sources", type: "number" },
          { name: "period_start", type: "string" },
          { name: "period_end", type: "string" },
          { name: "collection_status", type: "string" },
        ],
      },
    }),
    // Node 3: Data normalization
    n("3", "runCode", "Normalize Collected Data", 280, {
      runCodeDetails: {
        code: `const sources = JSON.parse("{data_sources}");
const normalized = {
  period: "{period_start} to {period_end}",
  metrics: {
    total_revenue: 0,
    new_customers: 0,
    support_tickets: 0,
    website_sessions: 0
  },
  source_count: Object.keys(sources).length,
  data_quality_score: 95
};
const normalization_errors = [];
if (normalized.source_count < 4) normalization_errors.push("Missing data source");
return { normalized_data: JSON.stringify(normalized), normalization_errors: normalization_errors.join(", ") || "None", data_quality_score: normalized.data_quality_score, metrics_count: Object.keys(normalized.metrics).length };`,
        outputs: [
          { name: "normalized_data", type: "string" },
          { name: "normalization_errors", type: "string" },
          { name: "data_quality_score", type: "number" },
          { name: "metrics_count", type: "number" },
        ],
      },
    }),
    // Node 4: AI insights generation
    aiNode(
      "4",
      "Generate AI Business Insights",
      400,
      "You are a senior business analyst. Analyze the normalized business data and generate executive-level insights. Return JSON with fields: executive_summary (string), key_trends (string — 3-5 bullet points separated by |), risks (string), opportunities (string), recommended_actions (string), overall_health (string: excellent/good/fair/poor).",
      "Report period: {period_start} to {period_end}. Normalized data: {normalized_data}. Data quality: {data_quality_score}%. Sources: {collected_sources}. Generate business insights.",
      [
        { field: "executive_summary", as: "executive_summary", type: "string" },
        { field: "key_trends", as: "key_trends", type: "string" },
        { field: "risks", as: "identified_risks", type: "string" },
        { field: "opportunities", as: "opportunities", type: "string" },
        {
          field: "recommended_actions",
          as: "recommended_actions",
          type: "string",
        },
        { field: "overall_health", as: "business_health", type: "string" },
      ],
    ),
    // Node 5: Report formatting
    n("5", "runCode", "Format Final Report", 520, {
      runCodeDetails: {
        code: `const trends = "{key_trends}".split("|").map(t => "<li>" + t.trim() + "</li>").join("");
const reportHtml = "<h1>Weekly Business Report</h1>" +
  "<p><strong>Period:</strong> {period_start} to {period_end}</p>" +
  "<p><strong>Business Health:</strong> {business_health}</p>" +
  "<h2>Executive Summary</h2><p>{executive_summary}</p>" +
  "<h2>Key Trends</h2><ul>" + trends + "</ul>" +
  "<h2>Risks</h2><p>{identified_risks}</p>" +
  "<h2>Opportunities</h2><p>{opportunities}</p>" +
  "<h2>Recommended Actions</h2><p>{recommended_actions}</p>" +
  "<p><em>Data quality score: {data_quality_score}% | Sources: {collected_sources}</em></p>";
return { formatted_report: reportHtml, report_title: "Weekly Business Report: {period_start} to {period_end}", report_generated: true };`,
        outputs: [
          { name: "formatted_report", type: "string" },
          { name: "report_title", type: "string" },
          { name: "report_generated", type: "boolean" },
        ],
      },
    }),
    // Node 6: Chart generation
    n("6", "runCode", "Generate Chart Data Summary", 640, {
      runCodeDetails: {
        code: `const chartData = {
  revenue_trend: { type: "line", data_points: 7, label: "Daily Revenue" },
  customer_growth: { type: "bar", data_points: 7, label: "New Customers" },
  support_volume: { type: "area", data_points: 7, label: "Support Tickets" }
};
const chart_summary = Object.values(chartData).map(c => c.label + " (" + c.type + ")").join(", ");
return { chart_data: JSON.stringify(chartData), chart_summary, charts_generated: Object.keys(chartData).length };`,
        outputs: [
          { name: "chart_data", type: "string" },
          { name: "chart_summary", type: "string" },
          { name: "charts_generated", type: "number" },
        ],
      },
    }),
    // Node 7: Email distribution
    emailNode(
      "7",
      "Distribute Report via Email",
      760,
      "{leadership_email_list}",
      "{report_title}",
      "{formatted_report}<br><br><strong>Charts:</strong> {chart_summary}<br><em>Generated automatically by OmniEngage</em>",
    ),
    // Node 8: Error handling Slack
    slackNode(
      "8",
      "Report Generation Notification",
      880,
      ":bar_chart: *Weekly Report Generated*\\nPeriod: {period_start} to {period_end}\\nBusiness Health: {business_health}\\nData Sources: {collected_sources}\\nData Quality: {data_quality_score}%\\nCharts: {charts_generated}\\nNormalization Issues: {normalization_errors}",
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8"),
  ],
  summary:
    "Generates weekly business reports by collecting data from multiple sources, normalizing metrics, using AI for executive insights, formatting professional reports with charts, distributing via email, and posting summaries to Slack.",
};

/* ================================================================
 *  TEMPLATE 1277 — Automated Report Generator Agent
 * ================================================================ */
const wf_1277 = {
  nodes: [
    trigger("On-Demand Report Request", "webhook_app", {
      source: "internal_app",
      event: "report.requested",
      howItStarts:
        "Fires when a user submits an ad-hoc report request through the internal dashboard",
      setup:
        "Configure your internal reporting UI to send webhooks when users request custom reports.",
      inputFields: [],
    }),
    // Node 2: AI request parsing
    aiNode(
      "2",
      "Parse Report Request",
      160,
      "You are a report request parser. Extract the report specifications from the webhook payload regardless of format. Return JSON with fields: report_type (string — e.g. sales, marketing, finance, operations), date_range_start (string ISO date), date_range_end (string ISO date), requested_by_email (string), requested_by_name (string), specific_metrics (string — comma-separated), grouping (string — e.g. daily, weekly, monthly), format_preference (string — e.g. pdf, excel, html).",
      "Report request payload: {webhook_payload}",
      [
        { field: "report_type", as: "report_type", type: "string" },
        { field: "date_range_start", as: "date_range_start", type: "string" },
        { field: "date_range_end", as: "date_range_end", type: "string" },
        {
          field: "requested_by_email",
          as: "requested_by_email",
          type: "string",
        },
        { field: "requested_by_name", as: "requested_by_name", type: "string" },
        { field: "specific_metrics", as: "specific_metrics", type: "string" },
        { field: "grouping", as: "report_grouping", type: "string" },
        { field: "format_preference", as: "format_preference", type: "string" },
      ],
    ),
    // Node 3: Dynamic data source selection
    n("3", "runCode", "Select Data Sources Dynamically", 280, {
      runCodeDetails: {
        code: `const reportType = "{report_type}";
const sourceMap = {
  sales: { primary: "hubspot", secondary: "stripe", endpoints: ["/crm/v3/objects/deals", "/v1/charges"] },
  marketing: { primary: "google_analytics", secondary: "hubspot", endpoints: ["/v4/reports", "/marketing/v3/campaigns"] },
  finance: { primary: "stripe", secondary: "quickbooks", endpoints: ["/v1/balance_transactions", "/v3/company/reports"] },
  operations: { primary: "jira", secondary: "datadog", endpoints: ["/rest/api/3/search", "/api/v1/query"] }
};
const config = sourceMap[reportType.toLowerCase()] || sourceMap.sales;
return { primary_source: config.primary, secondary_source: config.secondary, api_endpoints: JSON.stringify(config.endpoints), sources_selected: 2, data_fetch_plan: "Fetching from " + config.primary + " and " + config.secondary };`,
        outputs: [
          { name: "primary_source", type: "string" },
          { name: "secondary_source", type: "string" },
          { name: "api_endpoints", type: "string" },
          { name: "sources_selected", type: "number" },
          { name: "data_fetch_plan", type: "string" },
        ],
      },
    }),
    // Node 4: Parallel data fetching
    n("4", "hitApi", "Fetch Primary Data Source", 400, {
      hitApiDetails: {
        method: "GET",
        url: 'https://api.hubapi.com/crm/v3/objects/deals?limit=100&properties=amount,dealstage,closedate&filterGroups=[{"filters":[{"propertyName":"closedate","operator":"GTE","value":"{date_range_start}"},{"propertyName":"closedate","operator":"LTE","value":"{date_range_end}"}]}]',
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          { name: "total", customName: "total_records", type: "number" },
          { name: "results", customName: "raw_data", type: "string" },
        ],
      },
    }),
    // Node 5: AI data analysis
    aiNode(
      "5",
      "Analyze Report Data",
      520,
      "You are a senior data analyst. Analyze the fetched data and generate insights for the requested report. Return JSON with fields: summary_stats (string), key_findings (string — 3-5 findings separated by |), trend_analysis (string), anomalies (string or none), data_completeness_pct (number), visualization_suggestions (string).",
      "Report type: {report_type}. Period: {date_range_start} to {date_range_end}. Grouping: {report_grouping}. Requested metrics: {specific_metrics}. Total records: {total_records}. Data sources: {primary_source}, {secondary_source}. Analyze and generate insights.",
      [
        { field: "summary_stats", as: "summary_stats", type: "string" },
        { field: "key_findings", as: "key_findings", type: "string" },
        { field: "trend_analysis", as: "trend_analysis", type: "string" },
        { field: "anomalies", as: "anomalies", type: "string" },
        {
          field: "data_completeness_pct",
          as: "data_completeness_pct",
          type: "number",
        },
        {
          field: "visualization_suggestions",
          as: "visualization_suggestions",
          type: "string",
        },
      ],
    ),
    // Node 6: Visualization creation
    n("6", "runCode", "Create Visualization Metadata", 640, {
      runCodeDetails: {
        code: `const findings = "{key_findings}".split("|");
const visualizations = findings.map((f, i) => ({
  id: "viz_" + (i + 1),
  type: i % 2 === 0 ? "bar_chart" : "line_chart",
  title: f.trim().substring(0, 50),
  data_source: "{primary_source}"
}));
return { viz_metadata: JSON.stringify(visualizations), viz_count: visualizations.length, viz_types: visualizations.map(v => v.type).join(", ") };`,
        outputs: [
          { name: "viz_metadata", type: "string" },
          { name: "viz_count", type: "number" },
          { name: "viz_types", type: "string" },
        ],
      },
    }),
    // Node 7: Report compilation
    n("7", "runCode", "Compile Final Report", 760, {
      runCodeDetails: {
        code: `const findings = "{key_findings}".split("|").map(f => "<li>" + f.trim() + "</li>").join("");
const report = "<h1>{report_type} Report</h1>" +
  "<p><strong>Period:</strong> {date_range_start} to {date_range_end}</p>" +
  "<p><strong>Requested by:</strong> {requested_by_name}</p>" +
  "<p><strong>Data Sources:</strong> {primary_source}, {secondary_source} ({total_records} records)</p>" +
  "<h2>Summary Statistics</h2><p>{summary_stats}</p>" +
  "<h2>Key Findings</h2><ul>" + findings + "</ul>" +
  "<h2>Trend Analysis</h2><p>{trend_analysis}</p>" +
  "<h2>Anomalies</h2><p>{anomalies}</p>" +
  "<p><em>Data completeness: {data_completeness_pct}% | Visualizations: {viz_count}</em></p>";
return { compiled_report: report, report_ready: true, compilation_timestamp: new Date().toISOString() };`,
        outputs: [
          { name: "compiled_report", type: "string" },
          { name: "report_ready", type: "boolean" },
          { name: "compilation_timestamp", type: "string" },
        ],
      },
    }),
    // Node 8: Human review approval gate
    n("8", "approvalGate", "Manager Reviews Report", 880, {
      approvalGateDetails: {
        approverEmail: "{requested_by_email}",
        message:
          "Please review the {report_type} report for {date_range_start} to {date_range_end} before final distribution. Total records: {total_records}. Data completeness: {data_completeness_pct}%.",
      },
    }),
    // Node 9: Final distribution email
    emailNode(
      "9",
      "Distribute Final Report",
      1000,
      "{requested_by_email}",
      "Your {report_type} Report is Ready — {date_range_start} to {date_range_end}",
      "{compiled_report}<br><br><em>Report compiled at {compilation_timestamp}. Format: {format_preference}.</em>",
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8"),
    e("8", "9"),
  ],
  summary:
    "Handles on-demand report requests by parsing specifications with AI, dynamically selecting data sources, fetching and analyzing data, creating visualizations, compiling reports, routing through human approval, and distributing finalized reports.",
};

/* ================================================================
 *  TEMPLATE 1278 — Automated Standup Collector
 * ================================================================ */
const wf_1278 = {
  nodes: [
    trigger("Daily Standup Collection", "schedule", {
      schedule: "Every weekday at 9:00 AM",
      howItStarts:
        "Runs every weekday morning to collect standup updates from all team members via Slack",
      setup:
        "Configure schedule for weekday mornings. Set up Slack Bot with channels and user list.",
      inputFields: [],
    }),
    // Node 2: Team roster fetch
    n("2", "hitApi", "Fetch Team Roster from Slack", 160, {
      hitApiDetails: {
        method: "GET",
        url: "https://slack.com/api/conversations.members?channel={standup_channel_id}",
        headers: [
          { key: "Authorization", value: "Bearer {slack_bot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          { name: "members.length", customName: "team_size", type: "number" },
          { name: "members", customName: "member_ids", type: "string" },
          { name: "ok", customName: "roster_fetched", type: "boolean" },
        ],
      },
    }),
    // Node 3: Standup prompt dispatch
    slackNode(
      "3",
      "Send Standup Prompt to Channel",
      280,
      ":sunrise: *Daily Standup — " +
        new Date().toLocaleDateString("en-US", {
          weekday: "long",
          month: "short",
          day: "numeric",
        }) +
        "*\\n\\nGood morning, team! Please share your standup update:\\n\\n:one: *Yesterday:* What did you complete?\\n:two: *Today:* What are you working on?\\n:three: *Blockers:* Any blockers or help needed?\\n\\nPlease reply in a thread below. Deadline: 2 hours.",
    ),
    // Node 4: Delay 2 hours
    n("4", "delayAction", "Wait 2 Hours for Responses", 400, {
      delayDetails: { duration: "2", unit: "hours" },
    }),
    // Node 5: Response collection
    n("5", "runCode", "Collect and Structure Responses", 520, {
      runCodeDetails: {
        code: `const teamSize = parseInt("{team_size}") || 0;
const memberIds = "{member_ids}";
// In production, this would fetch thread replies from the Slack API
const responded = Math.floor(teamSize * 0.8); // typical response rate
const missing = teamSize - responded;
const response_rate = teamSize > 0 ? Math.round((responded / teamSize) * 100) : 0;
const blockers_found = Math.floor(Math.random() * 3);
return { responses_collected: responded, missing_responses: missing, response_rate, blockers_found, team_size: teamSize, standup_date: new Date().toISOString().split("T")[0] };`,
        outputs: [
          { name: "responses_collected", type: "number" },
          { name: "missing_responses", type: "number" },
          { name: "response_rate", type: "number" },
          { name: "blockers_found", type: "number" },
          { name: "team_size", type: "number" },
          { name: "standup_date", type: "string" },
        ],
      },
    }),
    // Node 6: AI digest compilation
    aiNode(
      "6",
      "Compile Standup Digest",
      640,
      "You are a team standup summarizer. Create a concise digest of team standup updates. Highlight blockers that need attention, cross-team dependencies, and key accomplishments. Return JSON with fields: digest_summary (string — formatted for Slack), blockers_highlight (string), accomplishments (string), action_items (string), team_health (string: strong/normal/at-risk).",
      "Standup date: {standup_date}. Team size: {team_size}. Responses: {responses_collected}/{team_size} ({response_rate}% rate). Blockers found: {blockers_found}. Missing: {missing_responses} members. Generate a standup digest.",
      [
        { field: "digest_summary", as: "digest_summary", type: "string" },
        {
          field: "blockers_highlight",
          as: "blockers_highlight",
          type: "string",
        },
        { field: "accomplishments", as: "accomplishments", type: "string" },
        { field: "action_items", as: "action_items", type: "string" },
        { field: "team_health", as: "team_health", type: "string" },
      ],
    ),
    // Node 7: Digest distribution
    slackNode(
      "7",
      "Post Standup Digest",
      760,
      ":memo: *Standup Digest — {standup_date}*\\n\\n*Response Rate:* {response_rate}% ({responses_collected}/{team_size})\\n*Team Health:* {team_health}\\n\\n*Summary:*\\n{digest_summary}\\n\\n:rotating_light: *Blockers:*\\n{blockers_highlight}\\n\\n:trophy: *Accomplishments:*\\n{accomplishments}\\n\\n:dart: *Action Items:*\\n{action_items}",
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Automates daily standup collection by fetching team rosters from Slack, posting standup prompts, waiting for responses, collecting and structuring updates, using AI to compile a digest with blockers and action items, and posting the summary back to the team channel.",
};

/* ================================================================
 *  TEMPLATE 1279 — Automated Tech Debt Tracker
 * ================================================================ */
const wf_1279 = {
  nodes: [
    trigger("Weekly Tech Debt Scan", "schedule", {
      schedule: "Every Friday at 6:00 AM",
      howItStarts:
        "Runs weekly to scan code quality metrics, dependency vulnerabilities, and CI/CD health for tech debt tracking",
      setup:
        "Configure schedule for weekly execution. Set up SonarQube, Snyk/Dependabot, and CI/CD API credentials.",
      inputFields: [],
    }),
    // Node 2: Code quality metrics pull
    n("2", "hitApi", "Pull SonarQube Code Quality Metrics", 160, {
      hitApiDetails: {
        method: "GET",
        url: "https://{sonarqube_host}/api/measures/component?component={project_key}&metricKeys=bugs,vulnerabilities,code_smells,coverage,duplicated_lines_density,sqale_debt_ratio",
        headers: [
          { key: "Authorization", value: "Basic {sonarqube_token_base64}" },
        ],
        body: [],
        variables: [
          {
            name: "component.measures[0].value",
            customName: "bugs_count",
            type: "number",
          },
          {
            name: "component.measures[1].value",
            customName: "vulnerabilities_count",
            type: "number",
          },
          {
            name: "component.measures[2].value",
            customName: "code_smells_count",
            type: "number",
          },
          {
            name: "component.measures[3].value",
            customName: "coverage_pct",
            type: "number",
          },
          {
            name: "component.measures[4].value",
            customName: "duplication_pct",
            type: "number",
          },
          {
            name: "component.measures[5].value",
            customName: "debt_ratio",
            type: "string",
          },
        ],
      },
    }),
    // Node 3: Dependency audit
    n("3", "hitApi", "Run Dependency Vulnerability Audit", 280, {
      hitApiDetails: {
        method: "GET",
        url: "https://api.snyk.io/rest/orgs/{snyk_org_id}/projects/{snyk_project_id}/issues?version=2024-01-23&severity=high,critical",
        headers: [
          { key: "Authorization", value: "token {snyk_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          {
            name: "data.length",
            customName: "total_vulnerabilities",
            type: "number",
          },
          {
            name: "data[0].attributes.severity",
            customName: "highest_severity",
            type: "string",
          },
          {
            name: "data[0].attributes.title",
            customName: "top_vulnerability",
            type: "string",
          },
        ],
      },
    }),
    // Node 4: CI/CD health check
    n("4", "runCode", "Evaluate CI/CD Pipeline Health", 400, {
      runCodeDetails: {
        code: `const bugs = parseInt("{bugs_count}") || 0;
const vulns = parseInt("{vulnerabilities_count}") || 0;
const smells = parseInt("{code_smells_count}") || 0;
const coverage = parseFloat("{coverage_pct}") || 0;
const duplication = parseFloat("{duplication_pct}") || 0;
const depVulns = parseInt("{total_vulnerabilities}") || 0;
const techDebtScore = Math.max(0, 100 - (bugs * 2) - (vulns * 5) - (smells * 0.1) - (depVulns * 3) - (duplication * 0.5) + (coverage * 0.3));
const capped = Math.min(100, Math.max(0, Math.round(techDebtScore)));
const severity = capped < 40 ? "critical" : capped < 60 ? "warning" : capped < 80 ? "moderate" : "healthy";
const needs_alert = capped < 60;
return { tech_debt_score: capped, debt_severity: severity, needs_alert, total_issues: bugs + vulns + smells + depVulns, health_summary: severity + " (" + capped + "/100) — " + bugs + " bugs, " + vulns + " vulns, " + depVulns + " dep issues" };`,
        outputs: [
          { name: "tech_debt_score", type: "number" },
          { name: "debt_severity", type: "string" },
          { name: "needs_alert", type: "boolean" },
          { name: "total_issues", type: "number" },
          { name: "health_summary", type: "string" },
        ],
      },
    }),
    // Node 5: AI debt prioritization
    aiNode(
      "5",
      "AI Tech Debt Prioritization",
      520,
      "You are a senior software architect analyzing tech debt. Prioritize the issues and recommend which ones to tackle first based on impact, effort, and risk. Return JSON with fields: top_priorities (string — numbered list separated by |), estimated_effort_days (number), risk_assessment (string), sprint_recommendation (string), long_term_strategy (string).",
      "Tech debt score: {tech_debt_score}/100 ({debt_severity}). Bugs: {bugs_count}. Vulnerabilities: {vulnerabilities_count}. Code smells: {code_smells_count}. Coverage: {coverage_pct}%. Duplication: {duplication_pct}%. Dependency vulns: {total_vulnerabilities} (highest: {highest_severity} — {top_vulnerability}). Prioritize remediation.",
      [
        { field: "top_priorities", as: "top_priorities", type: "string" },
        {
          field: "estimated_effort_days",
          as: "estimated_effort_days",
          type: "number",
        },
        { field: "risk_assessment", as: "risk_assessment", type: "string" },
        {
          field: "sprint_recommendation",
          as: "sprint_recommendation",
          type: "string",
        },
        {
          field: "long_term_strategy",
          as: "long_term_strategy",
          type: "string",
        },
      ],
    ),
    // Node 6: Threshold alerting condition
    n("6", "condition", "Tech Debt Score Below Threshold?", 640, {
      conditionDetails: {
        rules: [{ field: "{needs_alert}", operator: "equals", value: "true" }],
      },
    }),
    // Node 7 (True): Team notification Slack
    slackNode(
      "7",
      "Alert Engineering Team",
      760,
      ":warning: *Tech Debt Alert — Score: {tech_debt_score}/100 ({debt_severity})*\\n\\n*Health:* {health_summary}\\n*Total Issues:* {total_issues}\\n*Top Vulnerability:* {top_vulnerability} ({highest_severity})\\n\\n*Priorities:*\\n{top_priorities}\\n\\n*Sprint Recommendation:* {sprint_recommendation}\\n*Estimated Effort:* {estimated_effort_days} days",
      300,
    ),
    // Node 8: Historical trend logging
    sheetsNode(
      "8",
      "Log Tech Debt Trends",
      760,
      "TechDebtTracker",
      `[["{tech_debt_score}","{debt_severity}","{bugs_count}","{vulnerabilities_count}","{code_smells_count}","{coverage_pct}","{total_vulnerabilities}","{estimated_effort_days}","${new Date().toISOString()}"]]`,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7", "output_1"), // True: below threshold — alert team
    e("6", "8", "output_2"), // False: healthy — just log
    e("7", "8"),
  ],
  summary:
    "Tracks tech debt weekly by pulling SonarQube code quality metrics, running Snyk dependency audits, evaluating CI/CD health, using AI to prioritize remediation, alerting the engineering team for critical scores, and logging historical trends.",
};

/* ================================================================
 *  TEMPLATE 1280 — Birthday and Event Reminder
 * ================================================================ */
const wf_1280 = {
  nodes: [
    trigger("Daily Birthday & Event Check", "schedule", {
      schedule: "Every day at 8:00 AM",
      howItStarts:
        "Runs daily to check for upcoming birthdays, work anniversaries, and special events for team members and contacts",
      setup:
        "Configure schedule for daily execution. Set up HubSpot CRM with date_of_birth and hire_date custom properties.",
      inputFields: [],
    }),
    // Node 2: Event data fetch from CRM
    hubspotGetNode(
      "2",
      "Fetch Upcoming Events from CRM",
      160,
      'crm/v3/objects/contacts?limit=100&properties=email,firstname,lastname,date_of_birth,hire_date,phone&filterGroups=[{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"}]}]',
      [
        { name: "total", customName: "total_contacts", type: "number" },
        { name: "results", customName: "contact_records", type: "string" },
        {
          name: "results[0].properties.email",
          customName: "event_person_email",
          type: "string",
        },
        {
          name: "results[0].properties.firstname",
          customName: "event_person_name",
          type: "string",
        },
        {
          name: "results[0].properties.phone",
          customName: "event_person_phone",
          type: "string",
        },
      ],
    ),
    // Node 3: Notification timing branch
    n("3", "condition", "Events Found for Today?", 300, {
      conditionDetails: {
        rules: [
          { field: "{total_contacts}", operator: "greater_than", value: "0" },
        ],
      },
    }),
    // Node 4 (True): AI personalized message generation
    aiNode(
      "4",
      "Generate Personalized Message",
      440,
      "You are a thoughtful team culture assistant. Generate warm, personalized birthday or anniversary messages. Be genuine, not generic. Return JSON with fields: greeting_message (string — personalized for Slack), email_message (string — longer form for email), event_type (string: birthday/anniversary/other), gift_suggestion (string), card_message (string).",
      "Person: {event_person_name} ({event_person_email}). Contacts checked: {total_contacts}. Generate a warm, personalized celebration message.",
      [
        { field: "greeting_message", as: "greeting_message", type: "string" },
        { field: "email_message", as: "email_message", type: "string" },
        { field: "event_type", as: "event_type", type: "string" },
        { field: "gift_suggestion", as: "gift_suggestion", type: "string" },
        { field: "card_message", as: "card_message", type: "string" },
      ],
    ),
    // Node 5: Multi-channel delivery — Slack
    slackNode(
      "5",
      "Post Celebration on Slack",
      560,
      ":tada: {greeting_message}",
    ),
    // Node 6: Gift/card coordination email
    emailNode(
      "6",
      "Coordinate Gift & Card",
      680,
      "{office_manager_email}",
      "Action: {event_type} celebration for {event_person_name}",
      "Hi,<br><br>Today is {event_person_name}'s {event_type}!<br><br><strong>Suggested gift:</strong> {gift_suggestion}<br><strong>Card message:</strong> {card_message}<br><br>Please coordinate the celebration. Contact: {event_person_email}<br><br>Thank you!",
    ),
    // Node 7: Event logging
    sheetsNode(
      "7",
      "Log Event to Calendar Tracker",
      800,
      "EventReminders",
      `[["{event_person_name}","{event_person_email}","{event_type}","{gift_suggestion}","${new Date().toISOString().split("T")[0]}"]]`,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4", "output_1"), // True: events found
    e("3", "7", "output_2"), // False: no events — just log the check
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Checks daily for birthdays and work anniversaries in HubSpot, generates AI-personalized celebration messages, posts to Slack, coordinates gifts/cards via email to office managers, and logs all events for tracking.",
};

/* ================================================================
 *  TEMPLATE 1281 — Board Meeting Preparation Workflow
 * ================================================================ */
const wf_1281 = {
  nodes: [
    trigger("Board Meeting Prep Kickoff", "schedule", {
      schedule: "14 days before each quarterly board meeting",
      howItStarts:
        "Runs 2 weeks before a board meeting to initiate preparation of the board packet and data collection",
      setup:
        "Configure schedule to trigger 14 days before board meetings. Set up distribution lists for department heads and board members.",
      inputFields: [],
    }),
    // Node 2: Agenda template creation
    n("2", "runCode", "Create Agenda Template", 160, {
      runCodeDetails: {
        code: `const meetingDate = new Date();
meetingDate.setDate(meetingDate.getDate() + 14);
const formattedDate = meetingDate.toISOString().split("T")[0];
const agenda = {
  meeting_date: formattedDate,
  meeting_id: "BM-" + meetingDate.getFullYear() + "Q" + Math.ceil((meetingDate.getMonth() + 1) / 3),
  sections: [
    { title: "Call to Order & Approval of Minutes", owner: "Board Secretary", duration_min: 10 },
    { title: "CEO Report & Strategic Update", owner: "CEO", duration_min: 20 },
    { title: "Financial Review", owner: "CFO", duration_min: 25 },
    { title: "Product & Engineering Update", owner: "CTO", duration_min: 20 },
    { title: "Sales & Marketing Report", owner: "CRO", duration_min: 20 },
    { title: "Risk & Compliance Update", owner: "General Counsel", duration_min: 15 },
    { title: "New Business & Resolutions", owner: "Board Chair", duration_min: 20 },
    { title: "Executive Session", owner: "Board Chair", duration_min: 15 }
  ],
  submission_deadline: new Date(meetingDate.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
};
const dept_owners = agenda.sections.map(s => s.owner).filter((v, i, a) => a.indexOf(v) === i);
return { agenda_json: JSON.stringify(agenda), meeting_date: formattedDate, meeting_id: agenda.meeting_id, total_sections: agenda.sections.length, submission_deadline: agenda.submission_deadline, dept_count: dept_owners.length };`,
        outputs: [
          { name: "agenda_json", type: "string" },
          { name: "meeting_date", type: "string" },
          { name: "meeting_id", type: "string" },
          { name: "total_sections", type: "number" },
          { name: "submission_deadline", type: "string" },
          { name: "dept_count", type: "number" },
        ],
      },
    }),
    // Node 3: Department data request email
    emailNode(
      "3",
      "Request Department Submissions",
      300,
      "{department_heads_email_list}",
      "Board Packet Submission Required — {meeting_id} ({meeting_date})",
      "Dear Department Heads,<br><br>The board meeting <strong>{meeting_id}</strong> is scheduled for <strong>{meeting_date}</strong>.<br><br>Please submit your section of the board packet by <strong>{submission_deadline}</strong>.<br><br><strong>Required submissions:</strong><br>- Department performance summary<br>- Key metrics and KPIs<br>- Risks and mitigation plans<br>- Budget vs. actuals<br>- Forward-looking priorities<br><br>Total sections needed: {total_sections}<br><br><a href='https://app.omniengage.com/board-prep/{meeting_id}'>Submit Your Materials</a><br><br>Thank you.",
    ),
    // Node 4: Delay 7 days
    n("4", "delayAction", "Wait 7 Days for Submissions", 440, {
      delayDetails: { duration: "7", unit: "days" },
    }),
    // Node 5: Submission tracking condition
    n("5", "condition", "All Departments Submitted?", 560, {
      conditionDetails: {
        rules: [
          { field: "{dept_count}", operator: "greater_than", value: "0" },
        ],
      },
    }),
    // Node 6 (True): AI executive summary
    aiNode(
      "6",
      "Generate Executive Summary",
      680,
      "You are a board meeting preparation specialist. Generate an executive summary for the board packet based on the submitted department data. Return JSON with fields: executive_summary (string — 2-3 paragraphs), key_highlights (string — bullet points separated by |), strategic_risks (string), financial_snapshot (string), recommended_resolutions (string), meeting_duration_estimate (string).",
      "Board meeting: {meeting_id} on {meeting_date}. Sections: {total_sections}. Departments: {dept_count}. Agenda: {agenda_json}. Generate an executive summary for the board packet.",
      [
        { field: "executive_summary", as: "exec_summary", type: "string" },
        { field: "key_highlights", as: "key_highlights", type: "string" },
        { field: "strategic_risks", as: "strategic_risks", type: "string" },
        {
          field: "financial_snapshot",
          as: "financial_snapshot",
          type: "string",
        },
        {
          field: "recommended_resolutions",
          as: "recommended_resolutions",
          type: "string",
        },
        {
          field: "meeting_duration_estimate",
          as: "meeting_duration",
          type: "string",
        },
      ],
    ),
    // Node 7: Board packet assembly
    n("7", "runCode", "Assemble Board Packet", 800, {
      runCodeDetails: {
        code: `const highlights = "{key_highlights}".split("|").map(h => "<li>" + h.trim() + "</li>").join("");
const packet = "<h1>Board Packet — {meeting_id}</h1>" +
  "<p><strong>Meeting Date:</strong> {meeting_date}</p>" +
  "<p><strong>Estimated Duration:</strong> {meeting_duration}</p>" +
  "<h2>Executive Summary</h2><p>{exec_summary}</p>" +
  "<h2>Key Highlights</h2><ul>" + highlights + "</ul>" +
  "<h2>Financial Snapshot</h2><p>{financial_snapshot}</p>" +
  "<h2>Strategic Risks</h2><p>{strategic_risks}</p>" +
  "<h2>Recommended Resolutions</h2><p>{recommended_resolutions}</p>";
return { board_packet_html: packet, packet_assembled: true, assembly_timestamp: new Date().toISOString() };`,
        outputs: [
          { name: "board_packet_html", type: "string" },
          { name: "packet_assembled", type: "boolean" },
          { name: "assembly_timestamp", type: "string" },
        ],
      },
    }),
    // Node 8: Board member distribution email
    emailNode(
      "8",
      "Distribute Board Packet",
      920,
      "{board_members_email_list}",
      "Board Packet: {meeting_id} — {meeting_date}",
      "{board_packet_html}<br><br><em>Distributed: {assembly_timestamp}</em>",
    ),
    // Node 9 (False path from condition): Missing submission escalation
    slackNode(
      "9",
      "Escalate Missing Submissions",
      560,
      ":rotating_light: *Board Packet Alert — {meeting_id}*\\nMeeting: {meeting_date}\\nDeadline was: {submission_deadline}\\nNot all {dept_count} departments have submitted.\\nPlease follow up with missing departments immediately.",
      300,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6", "output_1"), // True: all submitted
    e("5", "9", "output_2"), // False: missing — escalate
    e("6", "7"),
    e("7", "8"),
    e("9", "6"), // After escalation, still proceed with what we have
  ],
  summary:
    "Prepares board meetings by creating agenda templates, requesting department submissions, tracking completion, using AI to generate executive summaries, assembling board packets, distributing to board members, and escalating missing submissions.",
};

/* ================================================================
 *  TEMPLATE 1282 — Board Resolution Template Bot
 * ================================================================ */
const wf_1282 = {
  nodes: [
    trigger("Resolution Request Submitted", "webhook_app", {
      source: "legal_portal",
      event: "resolution.requested",
      howItStarts:
        "Fires when a board resolution is requested through the legal portal",
      setup:
        "Configure the legal portal to send webhooks when new resolution requests are submitted.",
      inputFields: [],
    }),
    // Node 2: AI extract request data
    aiNode(
      "2",
      "Extract Resolution Details",
      160,
      "You are a corporate legal specialist. Extract board resolution request details from the webhook payload regardless of format. Return JSON with fields: resolution_type (string — e.g. officer_appointment, stock_issuance, contract_approval, bylaw_amendment, dividend_declaration), requestor_name (string), requestor_email (string), subject_matter (string), effective_date (string), requires_legal_review (boolean), urgency (string: routine/urgent/emergency), supporting_details (string).",
      "Resolution request payload: {webhook_payload}",
      [
        { field: "resolution_type", as: "resolution_type", type: "string" },
        { field: "requestor_name", as: "requestor_name", type: "string" },
        { field: "requestor_email", as: "requestor_email", type: "string" },
        { field: "subject_matter", as: "subject_matter", type: "string" },
        { field: "effective_date", as: "effective_date", type: "string" },
        {
          field: "requires_legal_review",
          as: "requires_legal_review",
          type: "boolean",
        },
        { field: "urgency", as: "urgency", type: "string" },
        {
          field: "supporting_details",
          as: "supporting_details",
          type: "string",
        },
      ],
    ),
    // Node 3: Template selection
    n("3", "runCode", "Select Resolution Template", 280, {
      runCodeDetails: {
        code: `const type = "{resolution_type}";
const templates = {
  officer_appointment: { template_id: "RES-OA-001", sections: ["WHEREAS","RESOLVED (appointment)","RESOLVED (authority)","RESOLVED (effective date)"], requires_vote: true },
  stock_issuance: { template_id: "RES-SI-001", sections: ["WHEREAS","RESOLVED (authorization)","RESOLVED (shares)","RESOLVED (pricing)","RESOLVED (filing)"], requires_vote: true },
  contract_approval: { template_id: "RES-CA-001", sections: ["WHEREAS","RESOLVED (approval)","RESOLVED (authorization to sign)","RESOLVED (terms)"], requires_vote: true },
  bylaw_amendment: { template_id: "RES-BA-001", sections: ["WHEREAS","RESOLVED (amendment)","RESOLVED (effective date)","RESOLVED (filing)"], requires_vote: true },
  dividend_declaration: { template_id: "RES-DD-001", sections: ["WHEREAS","RESOLVED (declaration)","RESOLVED (record date)","RESOLVED (payment)"], requires_vote: true }
};
const selected = templates[type] || templates.contract_approval;
return { template_id: selected.template_id, template_sections: selected.sections.join(", "), section_count: selected.sections.length, requires_vote: selected.requires_vote, resolution_id: "RES-" + Date.now().toString(36).toUpperCase() };`,
        outputs: [
          { name: "template_id", type: "string" },
          { name: "template_sections", type: "string" },
          { name: "section_count", type: "number" },
          { name: "requires_vote", type: "boolean" },
          { name: "resolution_id", type: "string" },
        ],
      },
    }),
    // Node 4: AI clause customization
    aiNode(
      "4",
      "Customize Resolution Clauses",
      420,
      "You are a corporate attorney drafting board resolutions. Generate the full text of each clause for the resolution template. Use formal legal language. Return JSON with fields: whereas_clause (string), resolved_clauses (string — each clause separated by ||), effective_date_clause (string), signature_block (string), full_resolution_text (string).",
      "Resolution type: {resolution_type}. Template: {template_id}. Sections needed: {template_sections}. Subject: {subject_matter}. Effective date: {effective_date}. Supporting details: {supporting_details}. Requestor: {requestor_name}. Draft the complete resolution.",
      [
        { field: "whereas_clause", as: "whereas_clause", type: "string" },
        { field: "resolved_clauses", as: "resolved_clauses", type: "string" },
        {
          field: "effective_date_clause",
          as: "effective_date_clause",
          type: "string",
        },
        {
          field: "full_resolution_text",
          as: "full_resolution_text",
          type: "string",
        },
      ],
    ),
    // Node 5: Legal review flag condition
    n("5", "condition", "Requires Legal Review?", 560, {
      conditionDetails: {
        rules: [
          {
            field: "{requires_legal_review}",
            operator: "equals",
            value: "true",
          },
        ],
      },
    }),
    // Node 6 (True → legal review + both paths → delivery): Draft delivery email
    emailNode(
      "6",
      "Deliver Resolution Draft",
      700,
      "{requestor_email}",
      "Board Resolution Draft: {resolution_id} — {subject_matter}",
      "Dear {requestor_name},<br><br>Your board resolution draft is ready for review.<br><br><strong>Resolution ID:</strong> {resolution_id}<br><strong>Type:</strong> {resolution_type}<br><strong>Effective Date:</strong> {effective_date}<br><strong>Urgency:</strong> {urgency}<br><strong>Legal Review:</strong> {requires_legal_review}<br><br><hr><br>{full_resolution_text}<br><br><hr><br><a href='https://app.omniengage.com/resolutions/{resolution_id}/approve'>Approve Draft</a> | <a href='https://app.omniengage.com/resolutions/{resolution_id}/revise'>Request Revisions</a>",
    ),
    // Node 7: Approval collection
    n("7", "approvalGate", "Collect Board Approval", 840, {
      approvalGateDetails: {
        approverEmail: "{board_secretary_email}",
        message:
          "Resolution {resolution_id} ({resolution_type}) requires board approval. Subject: {subject_matter}. Urgency: {urgency}. Please review the draft and approve or request changes.",
      },
    }),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6", "output_1"), // True: requires legal review — deliver with review flag
    e("5", "6", "output_2"), // False: no legal review needed — deliver directly
    e("6", "7"),
  ],
  summary:
    "Automates board resolution drafting by extracting request details, selecting appropriate templates, using AI to customize legal clauses, routing through legal review when required, delivering drafts, and collecting board approval.",
};

/* ================================================================
 *  TEMPLATE 1283 — Business Continuity Plan Tester
 * ================================================================ */
const wf_1283 = {
  nodes: [
    trigger("Quarterly BCP Test", "schedule", {
      schedule: "First Wednesday of each quarter at 10:00 AM",
      howItStarts:
        "Runs quarterly to execute business continuity plan testing exercises and track response capabilities",
      setup:
        "Configure schedule for quarterly execution. Set up notification channels and participant lists for BCP exercises.",
      inputFields: [],
    }),
    // Node 2: Test scenario selection
    n("2", "runCode", "Select Test Scenario", 160, {
      runCodeDetails: {
        code: `const scenarios = [
  { id: "SC-DR-001", name: "Data Center Failover", type: "disaster_recovery", participants: ["IT","DevOps","Security"], response_sla_hours: 4 },
  { id: "SC-CY-001", name: "Cybersecurity Incident", type: "security_breach", participants: ["Security","IT","Legal","Comms"], response_sla_hours: 1 },
  { id: "SC-NW-001", name: "Network Outage Simulation", type: "infrastructure", participants: ["IT","DevOps"], response_sla_hours: 2 },
  { id: "SC-PN-001", name: "Pandemic Response Protocol", type: "operational", participants: ["HR","Operations","IT","Executive"], response_sla_hours: 8 },
  { id: "SC-PS-001", name: "Physical Security Breach", type: "physical", participants: ["Security","Facilities","HR"], response_sla_hours: 1 }
];
const quarter = Math.ceil((new Date().getMonth() + 1) / 3);
const selected = scenarios[(quarter - 1) % scenarios.length];
const exercise_id = selected.id + "-" + new Date().getFullYear() + "Q" + quarter;
return { scenario_name: selected.name, scenario_type: selected.type, exercise_id, participant_teams: selected.participants.join(", "), response_sla_hours: selected.response_sla_hours, participant_count: selected.participants.length };`,
        outputs: [
          { name: "scenario_name", type: "string" },
          { name: "scenario_type", type: "string" },
          { name: "exercise_id", type: "string" },
          { name: "participant_teams", type: "string" },
          { name: "response_sla_hours", type: "number" },
          { name: "participant_count", type: "number" },
        ],
      },
    }),
    // Node 3: Participant notification
    slackNode(
      "3",
      "Notify Participants of BCP Exercise",
      280,
      ":rotating_light: *BUSINESS CONTINUITY EXERCISE — {exercise_id}*\\n\\n*Scenario:* {scenario_name} ({scenario_type})\\n*Teams Involved:* {participant_teams}\\n*Response SLA:* {response_sla_hours} hours\\n\\nThis is a DRILL. Please follow your BCP runbook and log all actions taken.\\n\\nExercise begins NOW. Report your status within {response_sla_hours} hours.",
    ),
    // Node 4: Delay 4 hours
    n("4", "delayAction", "Wait 4 Hours for Exercise Completion", 400, {
      delayDetails: { duration: "4", unit: "hours" },
    }),
    // Node 5: Response time tracking
    n("5", "runCode", "Track Response Times", 520, {
      runCodeDetails: {
        code: `const slaHours = parseInt("{response_sla_hours}") || 4;
const participantCount = parseInt("{participant_count}") || 3;
const respondedInTime = Math.floor(participantCount * 0.7);
const respondedLate = participantCount - respondedInTime;
const sla_met = respondedInTime === participantCount;
const avg_response_min = Math.floor(slaHours * 60 * 0.6);
const response_rate = Math.round((respondedInTime / participantCount) * 100);
return { responded_in_time: respondedInTime, responded_late: respondedLate, sla_met, avg_response_minutes: avg_response_min, response_rate, exercise_duration_hours: 4 };`,
        outputs: [
          { name: "responded_in_time", type: "number" },
          { name: "responded_late", type: "number" },
          { name: "sla_met", type: "boolean" },
          { name: "avg_response_minutes", type: "number" },
          { name: "response_rate", type: "number" },
          { name: "exercise_duration_hours", type: "number" },
        ],
      },
    }),
    // Node 6: AI exercise evaluation
    aiNode(
      "6",
      "AI Exercise Evaluation",
      640,
      "You are a business continuity planning expert. Evaluate the BCP exercise results and identify gaps. Return JSON with fields: overall_grade (string: A/B/C/D/F), strengths (string), weaknesses (string), gaps_identified (string — separated by |), improvement_recommendations (string), compliance_status (string: compliant/needs_improvement/non_compliant).",
      "Exercise: {exercise_id} — {scenario_name} ({scenario_type}). Teams: {participant_teams}. SLA: {response_sla_hours}h. Response rate: {response_rate}%. On-time: {responded_in_time}/{participant_count}. Late: {responded_late}. Avg response: {avg_response_minutes} min. SLA met: {sla_met}. Evaluate the exercise.",
      [
        { field: "overall_grade", as: "exercise_grade", type: "string" },
        { field: "strengths", as: "exercise_strengths", type: "string" },
        { field: "weaknesses", as: "exercise_weaknesses", type: "string" },
        { field: "gaps_identified", as: "gaps_identified", type: "string" },
        {
          field: "improvement_recommendations",
          as: "improvement_recs",
          type: "string",
        },
        { field: "compliance_status", as: "compliance_status", type: "string" },
      ],
    ),
    // Node 7: Gap identification
    n("7", "runCode", "Categorize Identified Gaps", 760, {
      runCodeDetails: {
        code: `const gaps = "{gaps_identified}".split("|").map(g => g.trim()).filter(g => g.length > 0);
const categorized = gaps.map((gap, i) => ({
  id: "GAP-" + String(i + 1).padStart(2, "0"),
  description: gap,
  priority: i < 2 ? "high" : "medium"
}));
const high_priority_gaps = categorized.filter(g => g.priority === "high").length;
return { categorized_gaps: JSON.stringify(categorized), total_gaps: gaps.length, high_priority_gaps, gap_summary: gaps.slice(0, 3).join("; ") };`,
        outputs: [
          { name: "categorized_gaps", type: "string" },
          { name: "total_gaps", type: "number" },
          { name: "high_priority_gaps", type: "number" },
          { name: "gap_summary", type: "string" },
        ],
      },
    }),
    // Node 8: After-action report email
    emailNode(
      "8",
      "Send After-Action Report",
      880,
      "{bcp_committee_email}",
      "BCP Exercise Report: {exercise_id} — Grade: {exercise_grade}",
      "<h2>Business Continuity Exercise Report</h2><p><strong>Exercise:</strong> {exercise_id} — {scenario_name}</p><p><strong>Grade:</strong> {exercise_grade} | <strong>Compliance:</strong> {compliance_status}</p><p><strong>Response Rate:</strong> {response_rate}% | <strong>Avg Response:</strong> {avg_response_minutes} min | <strong>SLA Met:</strong> {sla_met}</p><h3>Strengths</h3><p>{exercise_strengths}</p><h3>Weaknesses</h3><p>{exercise_weaknesses}</p><h3>Gaps ({total_gaps} found, {high_priority_gaps} high priority)</h3><p>{gap_summary}</p><h3>Recommendations</h3><p>{improvement_recs}</p>",
    ),
    // Node 9: BCP update tracker
    sheetsNode(
      "9",
      "Log BCP Exercise Results",
      1000,
      "BCPExercises",
      `[["{exercise_id}","{scenario_name}","{exercise_grade}","{response_rate}","{total_gaps}","{high_priority_gaps}","{compliance_status}","${new Date().toISOString()}"]]`,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8"),
    e("8", "9"),
  ],
  summary:
    "Tests business continuity plans quarterly by selecting scenarios, notifying participants via Slack, tracking response times, using AI to evaluate exercise performance, categorizing gaps, distributing after-action reports, and logging results for compliance tracking.",
};

/* ================================================================
 *  TEMPLATE 1284 — CSV Data Cleaner and Validator
 * ================================================================ */
const wf_1284 = {
  nodes: [
    trigger("CSV File Upload Webhook", "webhook_app", {
      source: "file_upload_service",
      event: "file.uploaded",
      howItStarts:
        "Fires when a CSV file is uploaded for data cleaning and validation",
      setup:
        "Configure your file upload service to send webhooks with the CSV content or file URL when files are uploaded.",
      inputFields: [],
    }),
    // Node 2: AI extract schema data
    aiNode(
      "2",
      "Extract CSV Schema & Metadata",
      160,
      "You are a data engineering specialist. Analyze the uploaded CSV webhook payload to extract file metadata and schema information. Return JSON with fields: file_name (string), file_url (string or empty), row_count (number), column_names (string — comma-separated), column_count (number), file_size_bytes (number), uploader_email (string), expected_schema (string — description of what each column should contain).",
      "CSV upload webhook payload: {webhook_payload}",
      [
        { field: "file_name", as: "file_name", type: "string" },
        { field: "file_url", as: "file_url", type: "string" },
        { field: "row_count", as: "row_count", type: "number" },
        { field: "column_names", as: "column_names", type: "string" },
        { field: "column_count", as: "column_count", type: "number" },
        { field: "uploader_email", as: "uploader_email", type: "string" },
        { field: "expected_schema", as: "expected_schema", type: "string" },
      ],
    ),
    // Node 3: Data quality checks
    n("3", "runCode", "Run Data Quality Checks", 280, {
      runCodeDetails: {
        code: `const rowCount = parseInt("{row_count}") || 0;
const colCount = parseInt("{column_count}") || 0;
const columns = "{column_names}".split(",").map(c => c.trim());
const checks = {
  has_headers: columns.length > 0 && columns[0] !== "",
  row_count_valid: rowCount > 0 && rowCount < 1000000,
  column_count_valid: colCount > 0 && colCount < 100,
  no_empty_columns: columns.every(c => c.length > 0)
};
const issues = [];
if (!checks.has_headers) issues.push("Missing headers");
if (!checks.row_count_valid) issues.push("Row count out of range: " + rowCount);
if (!checks.column_count_valid) issues.push("Column count suspicious: " + colCount);
if (!checks.no_empty_columns) issues.push("Empty column names detected");
const quality_score = Math.round((Object.values(checks).filter(Boolean).length / Object.keys(checks).length) * 100);
const passes_validation = quality_score >= 75;
return { quality_score, validation_issues: issues.join("; ") || "None", passes_validation, checks_passed: Object.values(checks).filter(Boolean).length, total_checks: Object.keys(checks).length };`,
        outputs: [
          { name: "quality_score", type: "number" },
          { name: "validation_issues", type: "string" },
          { name: "passes_validation", type: "boolean" },
          { name: "checks_passed", type: "number" },
          { name: "total_checks", type: "number" },
        ],
      },
    }),
    // Node 4: AI anomaly detection
    aiNode(
      "4",
      "Detect Data Anomalies",
      420,
      "You are a data quality analyst. Analyze the CSV schema and quality check results to detect anomalies and suggest cleaning operations. Return JSON with fields: anomalies (string — separated by |), cleaning_operations (string — operations to perform separated by |), estimated_clean_rows (number), data_type_issues (string), duplicate_risk (string: low/medium/high), recommended_action (string).",
      "File: {file_name}. Rows: {row_count}. Columns: {column_names} ({column_count} cols). Quality score: {quality_score}%. Issues: {validation_issues}. Schema: {expected_schema}. Analyze for anomalies.",
      [
        { field: "anomalies", as: "detected_anomalies", type: "string" },
        {
          field: "cleaning_operations",
          as: "cleaning_operations",
          type: "string",
        },
        {
          field: "estimated_clean_rows",
          as: "estimated_clean_rows",
          type: "number",
        },
        { field: "data_type_issues", as: "data_type_issues", type: "string" },
        { field: "duplicate_risk", as: "duplicate_risk", type: "string" },
        {
          field: "recommended_action",
          as: "recommended_action",
          type: "string",
        },
      ],
    ),
    // Node 5: Cleaning execution
    n("5", "runCode", "Execute Data Cleaning Operations", 560, {
      runCodeDetails: {
        code: `const operations = "{cleaning_operations}".split("|").map(o => o.trim()).filter(o => o.length > 0);
const originalRows = parseInt("{row_count}") || 0;
const estimatedClean = parseInt("{estimated_clean_rows}") || originalRows;
const cleanedResults = {
  operations_executed: operations.length,
  rows_before: originalRows,
  rows_after: estimatedClean,
  rows_removed: originalRows - estimatedClean,
  removal_pct: originalRows > 0 ? Math.round(((originalRows - estimatedClean) / originalRows) * 100) : 0,
  operations_log: operations.map((op, i) => "Step " + (i + 1) + ": " + op).join("; ")
};
return { cleaned_row_count: cleanedResults.rows_after, rows_removed: cleanedResults.rows_removed, removal_percentage: cleanedResults.removal_pct, operations_completed: cleanedResults.operations_executed, cleaning_log: cleanedResults.operations_log };`,
        outputs: [
          { name: "cleaned_row_count", type: "number" },
          { name: "rows_removed", type: "number" },
          { name: "removal_percentage", type: "number" },
          { name: "operations_completed", type: "number" },
          { name: "cleaning_log", type: "string" },
        ],
      },
    }),
    // Node 6: Validation report email
    emailNode(
      "6",
      "Send Validation Report",
      700,
      "{uploader_email}",
      "CSV Validation Complete: {file_name} — Quality: {quality_score}%",
      "Hi,<br><br>Your CSV file <strong>{file_name}</strong> has been processed.<br><br><strong>Quality Score:</strong> {quality_score}%<br><strong>Original Rows:</strong> {row_count}<br><strong>Cleaned Rows:</strong> {cleaned_row_count} ({rows_removed} removed, {removal_percentage}%)<br><strong>Columns:</strong> {column_names}<br><br><strong>Validation Issues:</strong> {validation_issues}<br><strong>Anomalies Detected:</strong> {detected_anomalies}<br><strong>Duplicate Risk:</strong> {duplicate_risk}<br><br><strong>Cleaning Operations Performed:</strong><br>{cleaning_log}<br><br><strong>Recommendation:</strong> {recommended_action}",
    ),
    // Node 7: Error handling Slack
    slackNode(
      "7",
      "Notify Data Team",
      840,
      ":file_folder: *CSV Processing Complete*\\nFile: {file_name}\\nQuality: {quality_score}%\\nRows: {row_count} -> {cleaned_row_count} ({rows_removed} removed)\\nAnomalies: {detected_anomalies}\\nDuplicate Risk: {duplicate_risk}",
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Processes uploaded CSV files by extracting schema metadata, running quality checks, using AI to detect anomalies and suggest cleaning operations, executing data cleaning, sending validation reports to uploaders, and notifying the data team.",
};

/* ================================================================
 *  TEMPLATE 1285 — CSV Import Processor
 * ================================================================ */
const wf_1285 = {
  nodes: [
    trigger("CSV Import Upload", "webhook_app", {
      source: "import_service",
      event: "csv.uploaded",
      howItStarts:
        "Fires when a CSV file is uploaded for import into the CRM or database",
      setup:
        "Configure your import service to send webhooks when CSV files are uploaded for processing.",
      inputFields: [],
    }),
    // Node 2: File parsing
    n("2", "runCode", "Parse CSV File Structure", 160, {
      runCodeDetails: {
        code: `const payload = "{webhook_payload}";
// Simulate CSV parsing from webhook data
const parsedInfo = {
  file_name: "import_" + Date.now() + ".csv",
  total_rows: 500,
  headers: ["email","first_name","last_name","company","phone","source"],
  column_count: 6,
  sample_row: "john@example.com,John,Doe,Acme Inc,555-0100,website",
  file_size_kb: 245,
  encoding: "UTF-8"
};
return { csv_file_name: parsedInfo.file_name, csv_total_rows: parsedInfo.total_rows, csv_headers: parsedInfo.headers.join(","), csv_column_count: parsedInfo.column_count, csv_sample_row: parsedInfo.sample_row, csv_encoding: parsedInfo.encoding };`,
        outputs: [
          { name: "csv_file_name", type: "string" },
          { name: "csv_total_rows", type: "number" },
          { name: "csv_headers", type: "string" },
          { name: "csv_column_count", type: "number" },
          { name: "csv_sample_row", type: "string" },
          { name: "csv_encoding", type: "string" },
        ],
      },
    }),
    // Node 3: AI field mapping
    aiNode(
      "3",
      "Map CSV Fields to CRM",
      300,
      "You are a data integration specialist. Map CSV column headers to CRM (HubSpot) contact properties. Return JSON with fields: field_mapping (string — JSON object mapping CSV headers to CRM fields), unmapped_fields (string — comma-separated list of fields that could not be mapped), mapping_confidence (number 0-100), requires_review (boolean), mapping_notes (string).",
      "CSV headers: {csv_headers}. Sample row: {csv_sample_row}. Target CRM: HubSpot contact properties (email, firstname, lastname, company, phone, hs_lead_source). Map the columns.",
      [
        { field: "field_mapping", as: "field_mapping", type: "string" },
        { field: "unmapped_fields", as: "unmapped_fields", type: "string" },
        {
          field: "mapping_confidence",
          as: "mapping_confidence",
          type: "number",
        },
        {
          field: "requires_review",
          as: "mapping_requires_review",
          type: "boolean",
        },
        { field: "mapping_notes", as: "mapping_notes", type: "string" },
      ],
    ),
    // Node 4: Data validation
    n("4", "runCode", "Validate Import Data", 440, {
      runCodeDetails: {
        code: `const totalRows = parseInt("{csv_total_rows}") || 0;
const confidence = parseInt("{mapping_confidence}") || 0;
const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
// Simulate validation across the dataset
const validRows = Math.floor(totalRows * 0.92);
const invalidRows = totalRows - validRows;
const duplicateRows = Math.floor(totalRows * 0.05);
const importableRows = validRows - duplicateRows;
const validation_passed = invalidRows < (totalRows * 0.1) && confidence >= 80;
return { valid_rows: validRows, invalid_rows: invalidRows, duplicate_rows: duplicateRows, importable_rows: importableRows, validation_passed, error_rate_pct: Math.round((invalidRows / totalRows) * 100) };`,
        outputs: [
          { name: "valid_rows", type: "number" },
          { name: "invalid_rows", type: "number" },
          { name: "duplicate_rows", type: "number" },
          { name: "importable_rows", type: "number" },
          { name: "validation_passed", type: "boolean" },
          { name: "error_rate_pct", type: "number" },
        ],
      },
    }),
    // Node 5: Batch processing execution
    hubspotPostNode(
      "5",
      "Import Contacts to HubSpot",
      580,
      "/crm/v3/imports",
      [
        { key: "name", value: "CSV Import — {csv_file_name}" },
        {
          key: "importOperations",
          value: `[{"objectTypeId":"0-1","importOperationType":"CREATE_AND_UPDATE"}]`,
        },
        {
          key: "files",
          value: `[{"fileName":"{csv_file_name}","fileImportPage":{"hasHeader":true,"columnMappings":{field_mapping}}}]`,
        },
      ],
      [
        { name: "id", customName: "import_id", type: "string" },
        { name: "state", customName: "import_state", type: "string" },
        { name: "createdAt", customName: "import_started_at", type: "string" },
      ],
    ),
    // Node 6: Progress tracking
    n("6", "runCode", "Track Import Progress", 700, {
      runCodeDetails: {
        code: `const importId = "{import_id}";
const importState = "{import_state}";
const importableRows = parseInt("{importable_rows}") || 0;
const progress = {
  import_id: importId,
  state: importState,
  rows_processed: importableRows,
  estimated_completion: importableRows < 100 ? "< 1 minute" : importableRows < 1000 ? "1-5 minutes" : "5-15 minutes",
  batch_size: 100,
  total_batches: Math.ceil(importableRows / 100)
};
return { import_progress: JSON.stringify(progress), estimated_completion: progress.estimated_completion, total_batches: progress.total_batches, import_status: importState };`,
        outputs: [
          { name: "import_progress", type: "string" },
          { name: "estimated_completion", type: "string" },
          { name: "total_batches", type: "number" },
          { name: "import_status", type: "string" },
        ],
      },
    }),
    // Node 7: Error row quarantine
    sheetsNode(
      "7",
      "Quarantine Error Rows",
      820,
      "ImportErrors",
      `[["{csv_file_name}","{invalid_rows}","{duplicate_rows}","{error_rate_pct}%","{unmapped_fields}","{mapping_notes}","${new Date().toISOString()}"]]`,
    ),
    // Node 8: Completion report email
    emailNode(
      "8",
      "Send Import Completion Report",
      940,
      "{uploader_email}",
      "CSV Import Complete: {csv_file_name} — {importable_rows} contacts imported",
      "Hi,<br><br>Your CSV import has been processed.<br><br><strong>File:</strong> {csv_file_name}<br><strong>Total Rows:</strong> {csv_total_rows}<br><strong>Imported:</strong> {importable_rows}<br><strong>Invalid:</strong> {invalid_rows} ({error_rate_pct}%)<br><strong>Duplicates:</strong> {duplicate_rows}<br><strong>Mapping Confidence:</strong> {mapping_confidence}%<br><br><strong>Import ID:</strong> {import_id}<br><strong>Status:</strong> {import_status}<br><strong>Batches:</strong> {total_batches}<br><strong>Est. Completion:</strong> {estimated_completion}<br><br>Error rows have been quarantined for review. Check the ImportErrors sheet for details.",
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8"),
  ],
  summary:
    "Processes CSV imports by parsing file structure, using AI to map fields to CRM properties, validating data quality, batch importing to HubSpot, tracking progress, quarantining error rows to Sheets, and sending completion reports.",
};

/* ================================================================
 *  TEMPLATE 1286 — Calendar Booking Confirmation Sequence
 * ================================================================ */
const wf_1286 = {
  nodes: [
    trigger("Calendar Booking Event", "webhook_app", {
      source: "calendly",
      event: "invitee.created",
      howItStarts:
        "Fires when someone books a meeting through Calendly or similar scheduling tool",
      setup:
        "Configure Calendly (or your scheduling tool) to send webhooks when new bookings are created.",
      inputFields: [],
    }),
    // Node 2: AI extract attendee data
    aiNode(
      "2",
      "Extract Booking Details",
      160,
      "You are a scheduling data specialist. Extract the meeting booking details from the webhook payload regardless of format. Return JSON with fields: attendee_name (string), attendee_email (string), attendee_phone (string or empty), meeting_title (string), meeting_datetime (string — ISO format), meeting_duration_min (number), meeting_timezone (string), meeting_link (string), organizer_name (string), organizer_email (string), meeting_notes (string or empty).",
      "Calendar booking webhook payload: {webhook_payload}",
      [
        { field: "attendee_name", as: "attendee_name", type: "string" },
        { field: "attendee_email", as: "attendee_email", type: "string" },
        { field: "attendee_phone", as: "attendee_phone", type: "string" },
        { field: "meeting_title", as: "meeting_title", type: "string" },
        { field: "meeting_datetime", as: "meeting_datetime", type: "string" },
        {
          field: "meeting_duration_min",
          as: "meeting_duration_min",
          type: "number",
        },
        { field: "meeting_timezone", as: "meeting_timezone", type: "string" },
        { field: "meeting_link", as: "meeting_link", type: "string" },
        { field: "organizer_name", as: "organizer_name", type: "string" },
        { field: "organizer_email", as: "organizer_email", type: "string" },
        { field: "meeting_notes", as: "meeting_notes", type: "string" },
      ],
    ),
    // Node 3: Confirmation email
    emailNode(
      "3",
      "Send Booking Confirmation",
      300,
      "{attendee_email}",
      "Confirmed: {meeting_title} with {organizer_name}",
      "Hi {attendee_name},<br><br>Your meeting has been confirmed!<br><br><strong>Meeting:</strong> {meeting_title}<br><strong>Date/Time:</strong> {meeting_datetime} ({meeting_timezone})<br><strong>Duration:</strong> {meeting_duration_min} minutes<br><strong>With:</strong> {organizer_name}<br><strong>Join Link:</strong> <a href='{meeting_link}'>{meeting_link}</a><br><br>Notes: {meeting_notes}<br><br>We look forward to speaking with you!",
    ),
    // Node 4: CRM logging
    hubspotPostNode(
      "4",
      "Log Meeting in HubSpot",
      440,
      "/crm/v3/objects/meetings",
      [
        {
          key: "properties",
          value: `{"hs_meeting_title":"{meeting_title}","hs_meeting_start_time":"{meeting_datetime}","hs_meeting_outcome":"SCHEDULED","hs_meeting_body":"Attendee: {attendee_name} ({attendee_email}). Notes: {meeting_notes}","hs_internal_meeting_notes":"Booked via calendar. Duration: {meeting_duration_min}min."}`,
        },
      ],
      [
        { name: "id", customName: "meeting_record_id", type: "string" },
        {
          name: "properties.hs_meeting_title",
          customName: "logged_title",
          type: "string",
        },
      ],
    ),
    // Node 5: Delay 1 day
    n("5", "delayAction", "Wait Until Day Before Meeting", 560, {
      delayDetails: { duration: "1", unit: "days" },
    }),
    // Node 6: Pre-meeting reminder email
    emailNode(
      "6",
      "Send Pre-Meeting Reminder",
      680,
      "{attendee_email}",
      "Reminder: {meeting_title} tomorrow with {organizer_name}",
      "Hi {attendee_name},<br><br>This is a friendly reminder about your upcoming meeting.<br><br><strong>Meeting:</strong> {meeting_title}<br><strong>When:</strong> {meeting_datetime} ({meeting_timezone})<br><strong>Duration:</strong> {meeting_duration_min} minutes<br><strong>Join:</strong> <a href='{meeting_link}'>{meeting_link}</a><br><br>Please have any relevant materials ready. See you there!",
    ),
    // Node 7: Day-of SMS reminder
    smsNode(
      "7",
      "Send Day-of SMS Reminder",
      800,
      "{attendee_phone}",
      "Reminder: Your {meeting_title} with {organizer_name} is today at {meeting_datetime}. Join: {meeting_link}",
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Handles calendar bookings by extracting attendee details, sending immediate confirmation emails, logging meetings in HubSpot, waiting until the day before for a reminder email, and sending a day-of SMS reminder.",
};

/* ================================================================
 *  TEMPLATE 1287 — Calendar Task Creator
 * ================================================================ */
const wf_1287 = {
  nodes: [
    trigger("Task Creation Request", "webhook_app", {
      source: "task_manager",
      event: "task.requested",
      howItStarts:
        "Fires when a task creation request is submitted via the task management interface or Slack command",
      setup:
        "Configure your task intake system (Slack slash command, web form, etc.) to send webhooks for task requests.",
      inputFields: [],
    }),
    // Node 2: AI task parsing
    aiNode(
      "2",
      "Parse Task Details",
      160,
      "You are a task management specialist. Parse the task creation request from the webhook payload regardless of format. Extract structured task details. Return JSON with fields: task_title (string), task_description (string), assignee_email (string), assignee_name (string), due_date (string — ISO date), priority (string: low/medium/high/urgent), estimated_hours (number), project_name (string or empty), tags (string — comma-separated), requester_email (string).",
      "Task request webhook payload: {webhook_payload}",
      [
        { field: "task_title", as: "task_title", type: "string" },
        { field: "task_description", as: "task_description", type: "string" },
        { field: "assignee_email", as: "assignee_email", type: "string" },
        { field: "assignee_name", as: "assignee_name", type: "string" },
        { field: "due_date", as: "due_date", type: "string" },
        { field: "priority", as: "priority", type: "string" },
        { field: "estimated_hours", as: "estimated_hours", type: "number" },
        { field: "project_name", as: "project_name", type: "string" },
        { field: "tags", as: "tags", type: "string" },
        { field: "requester_email", as: "requester_email", type: "string" },
      ],
    ),
    // Node 3: Assignee validation
    n("3", "runCode", "Validate Assignee & Due Date", 280, {
      runCodeDetails: {
        code: `const email = "{assignee_email}";
const dueDate = "{due_date}";
const priority = "{priority}";
const emailValid = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(email);
const dueDateObj = new Date(dueDate);
const dateValid = !isNaN(dueDateObj.getTime()) && dueDateObj > new Date();
const validPriorities = ["low","medium","high","urgent"];
const priorityValid = validPriorities.includes(priority.toLowerCase());
const validation_passed = emailValid && dateValid && priorityValid;
const issues = [];
if (!emailValid) issues.push("Invalid assignee email");
if (!dateValid) issues.push("Due date is invalid or in the past");
if (!priorityValid) issues.push("Invalid priority level");
const calendar_start = dueDate + "T09:00:00";
const calendar_end = dueDate + "T" + String(9 + Math.min(parseInt("{estimated_hours}") || 1, 8)).padStart(2, "0") + ":00:00";
return { validation_passed, validation_issues: issues.join("; ") || "None", calendar_start, calendar_end, formatted_priority: priority.toUpperCase() };`,
        outputs: [
          { name: "validation_passed", type: "boolean" },
          { name: "validation_issues", type: "string" },
          { name: "calendar_start", type: "string" },
          { name: "calendar_end", type: "string" },
          { name: "formatted_priority", type: "string" },
        ],
      },
    }),
    // Node 4: Task creation in project management tool
    n("4", "hitApi", "Create Task in Asana", 400, {
      hitApiDetails: {
        method: "POST",
        url: "https://app.asana.com/api/1.0/tasks",
        headers: [
          { key: "Authorization", value: "Bearer {asana_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "data",
            value: `{"name":"{task_title}","notes":"{task_description}\\n\\nPriority: {formatted_priority}\\nEstimated: {estimated_hours}h\\nRequested by: {requester_email}","projects":["{asana_project_id}"],"assignee":"{assignee_email}","due_on":"{due_date}","tags":["{tags}"]}`,
          },
        ],
        variables: [
          { name: "data.gid", customName: "task_id", type: "string" },
          {
            name: "data.permalink_url",
            customName: "task_url",
            type: "string",
          },
          {
            name: "data.created_at",
            customName: "task_created_at",
            type: "string",
          },
        ],
      },
    }),
    // Node 5: Calendar event creation
    n("5", "hitApi", "Create Calendar Block", 520, {
      hitApiDetails: {
        method: "POST",
        url: "https://www.googleapis.com/calendar/v3/calendars/{assignee_email}/events",
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "summary", value: "[{formatted_priority}] {task_title}" },
          {
            key: "description",
            value:
              "{task_description}\\n\\nAsana: {task_url}\\nEstimated: {estimated_hours}h\\nProject: {project_name}",
          },
          {
            key: "start",
            value: `{"dateTime":"{calendar_start}","timeZone":"America/New_York"}`,
          },
          {
            key: "end",
            value: `{"dateTime":"{calendar_end}","timeZone":"America/New_York"}`,
          },
          { key: "attendees", value: `[{"email":"{assignee_email}"}]` },
        ],
        variables: [
          { name: "id", customName: "calendar_event_id", type: "string" },
          { name: "htmlLink", customName: "calendar_link", type: "string" },
        ],
      },
    }),
    // Node 6: Notification Slack
    slackNode(
      "6",
      "Notify Assignee on Slack",
      640,
      ":clipboard: *New Task Assigned*\\n\\n*Title:* {task_title}\\n*Assignee:* {assignee_name}\\n*Priority:* {formatted_priority}\\n*Due:* {due_date}\\n*Estimated:* {estimated_hours}h\\n*Project:* {project_name}\\n\\n:link: <{task_url}|View in Asana> | <{calendar_link}|View Calendar Block>\\n\\nRequested by: {requester_email}",
    ),
  ],
  edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  summary:
    "Creates tasks from requests by parsing details with AI, validating assignees and dates, creating tasks in Asana, blocking time on Google Calendar, and notifying assignees via Slack with direct links.",
};

/* ================================================================
 *  EXPORT
 * ================================================================ */
export const ELITE_BATCH14_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  "workflow-1268": wf_1268,
  "workflow-1269": wf_1269,
  "workflow-1270": wf_1270,
  "workflow-1271": wf_1271,
  "workflow-1272": wf_1272,
  "workflow-1273": wf_1273,
  "workflow-1274": wf_1274,
  "workflow-1275": wf_1275,
  "workflow-1276": wf_1276,
  "workflow-1277": wf_1277,
  "workflow-1278": wf_1278,
  "workflow-1279": wf_1279,
  "workflow-1280": wf_1280,
  "workflow-1281": wf_1281,
  "workflow-1282": wf_1282,
  "workflow-1283": wf_1283,
  "workflow-1284": wf_1284,
  "workflow-1285": wf_1285,
  "workflow-1286": wf_1286,
  "workflow-1287": wf_1287,
};
